import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { supabase, extractTextFromFile } from "../../supabase";
import { callClaudeWithMessages } from "../../services/claude";
import { geocodeAddress } from "../../services/geocoding";
import { fmt } from "../../utils/formatters";
import { inputCls, Field } from "../common/FormHelpers";
import useMsaList from "../../hooks/useMsaList";
import { normalizeAddress } from "../../utils/addressKey";

// ── Active/Expired cutoffs ──────────────────────────────────────────────────
const LEASE_ACTIVE_MONTHS = 16;
const SALE_ACTIVE_MONTHS = 24;

function isActive(type, dateStr) {
  if (!dateStr) return true;
  const d = new Date(dateStr);
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - (type === "lease" ? LEASE_ACTIVE_MONTHS : SALE_ACTIVE_MONTHS));
  return d >= cutoff;
}

// ── Deduplication ───────────────────────────────────────────────────────────
// ── Fuzzy comparison helpers ────────────────────────────────────────────────
// Normalize a string for "is this the same?" matching: lowercase, strip
// punctuation, normalize US street suffixes (Ave/Avenue, Blvd/Boulevard),
// collapse whitespace. Catches typos like "LeJuene" vs "LeJeune" only via
// the buyer/price/SF fallback below; this normalizer catches the more
// common variants like "Avenue" vs "Ave".
// Address normalization is shared with the deal duplicate check — two copies
// of these lookup tables is how the same building ends up entered twice under
// different abbreviations. See utils/addressKey.js.
function normalizeText(s) {
  return (s || "").toLowerCase().replace(/\s+/g, " ").trim();
}

// Two sale comps are the same if either:
//  - normalized address + buyer + comp_date all match (catches Ave/Avenue), OR
//  - buyer + sale_price + building_sf + comp_date all match (catches typos
//    in the address itself, e.g. "LeJeune" vs "LeJuene").
function isDuplicateSale(existing, newComp) {
  const newAddr = normalizeAddress(newComp.address);
  const newBuyer = normalizeText(newComp.buyer);
  const newDate = newComp.comp_date || null;
  const newPrice = Number(newComp.sale_price) || null;
  const newSf = Number(newComp.building_sf) || null;
  return existing.some(c => {
    if (c.type !== "sale") return false;
    if (c.comp_date !== newDate) return false;
    if (normalizeText(c.buyer) !== newBuyer || !newBuyer) return false;
    // Path 1: address match
    if (normalizeAddress(c.address) === newAddr && newAddr) return true;
    // Path 2: price + SF match (allow ±1% tolerance on each)
    const cPrice = Number(c.sale_price) || null;
    const cSf = Number(c.building_sf) || null;
    if (newPrice && cPrice && newSf && cSf) {
      const priceClose = Math.abs(cPrice - newPrice) / Math.max(cPrice, newPrice) <= 0.01;
      const sfClose = Math.abs(cSf - newSf) / Math.max(cSf, newSf) <= 0.01;
      if (priceClose && sfClose) return true;
    }
    return false;
  });
}

// Two lease comps are the same if normalized address + tenant + comp_date
// all match. Lease comps don't have a price/SF combo as reliable as sales,
// so we only use the address path with the same suffix normalization.
function isDuplicateLease(existing, newComp) {
  const newAddr = normalizeAddress(newComp.address);
  const newTenant = normalizeText(newComp.tenant);
  const newDate = newComp.comp_date || null;
  return existing.some(c =>
    c.type === "lease" &&
    c.comp_date === newDate &&
    normalizeText(c.tenant) === newTenant && newTenant &&
    normalizeAddress(c.address) === newAddr && newAddr
  );
}

// ── Portfolio Detection ─────────────────────────────────────────────────────
function detectPortfolios(saleComps) {
  const groups = {};
  saleComps.forEach(c => {
    if (!c.buyer || !c.comp_date) return;
    const key = `${c.buyer.toLowerCase().trim()}_${c.comp_date}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(c);
  });
  return Object.values(groups).filter(g => g.length > 1);
}

// "Tracking" comps are pipeline deals we passed on but want to monitor — they
// have no realized price / cap and would skew aggregates if blended with closed
// trades. Detect by status_detail so we can filter them out by default.
const TRACKING_STATUS_DETAIL = "Tracking (Pipeline)";
function isTrackingComp(c) {
  return c?.status_detail === TRACKING_STATUS_DETAIL;
}

// ── Empty forms ─────────────────────────────────────────────────────────────
const EMPTY_LEASE = {
  property_name: "", address: "", city: "", state: "", market: "South Florida",
  micro_market: "",
  tenant: "", tenant_industry: "", building_sf: "", suite_sf: "",
  lease_rate: "", lease_type: "NNN", opex_psf: "", comp_date: "", landlord: "", notes: "", source: "",
  loading_type: "", door_type: "",
  year_built: "", clear_height: "",
};

const EMPTY_SALE = {
  property_name: "", address: "", city: "", state: "", market: "South Florida",
  micro_market: "",
  buyer: "", seller: "", sale_price: "", price_psf: "", building_sf: "",
  cap_rate: "", comp_date: "", portfolio_name: "", notes: "", source: "",
  occupancy_at_closing: "", walt_years: "",
  year_built: "", clear_height: "", office_finish_pct: "",
  status_detail: "",
};

// ── Reusable collapsible block (used in manual entry forms + drawer notes) ──
function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[rgba(58,58,58,0.4)] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-[rgba(44,44,44,0.6)] hover:bg-[#2c2c2c] transition-colors text-left"
      >
        <span className="text-xs font-bold uppercase tracking-wider text-[#8a8a8a]">{title}</span>
        <svg className={`w-4 h-4 text-[#8a8a8a] transition-transform ${open ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
      </button>
      {open && <div className="p-4 bg-[rgba(44,44,44,0.3)]">{children}</div>}
    </div>
  );
}

// ── Map pin icon used by the "View on Map" button across the comp database ──
function MapPinIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8zm0 11a3 3 0 110-6 3 3 0 010 6z" />
    </svg>
  );
}

// ── Drawer stat tile: small label + bold value, used inside the comp drawer ──
function DrawerStat({ label, value }) {
  return (
    <div className="rounded-lg px-3 py-2" style={{ backgroundColor: '#ffffff', border: '1px solid #d8d8d8' }}>
      <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#8a8a8a' }}>{label}</div>
      <div className="text-sm font-bold mt-0.5 truncate" style={{ color: '#333333' }}>{value}</div>
    </div>
  );
}

// ── Portfolio grid: groups of multi-asset sales by the same buyer on same date.
//    Each card is clickable and opens the portfolio detail modal via onSelect. ──
function PortfolioGrid({ portfolios, badge, sectionTitle, onSelect }) {
  return (
    <div className="mb-2">
      <h3 className={sectionTitle}>Portfolio Sales</h3>
      <div className="grid grid-cols-2 gap-3">
        {portfolios.map((group, i) => {
          const totalPrice = group.reduce((s, c) => s + (c.sale_price || 0), 0);
          const totalSF = group.reduce((s, c) => s + (Number(c.building_sf) || 0), 0);
          const avgPsf = totalSF > 0 ? Math.round(totalPrice / totalSF) : 0;
          const capRows = group.filter(c => c.cap_rate);
          const avgCap = capRows.length > 0
            ? (capRows.reduce((s, c) => s + (c.cap_rate || 0), 0) / capRows.length).toFixed(2)
            : null;
          return (
            <button
              key={i}
              onClick={() => onSelect && onSelect({ group, totalPrice, totalSF, avgPsf, avgCap })}
              className="text-left bg-[#2c2c2c] hover:bg-[#3a3a3a] border border-[rgba(233,53,41,0.3)] hover:border-[rgba(233,53,41,0.6)] rounded-lg p-4 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={badge("bg-[rgba(233,53,41,0.2)] text-[#f04f44]")}>Portfolio</span>
                <span className="text-white font-semibold text-sm">{group[0].buyer}</span>
                <span className="text-[#8a8a8a] text-xs">· {group[0].comp_date}</span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div><span className="text-[#8a8a8a]">Assets:</span> <span className="text-[#b4b4b4]">{group.length}</span></div>
                <div><span className="text-[#8a8a8a]">Total:</span> <span className="text-[#b4b4b4]">${Number(totalPrice).toLocaleString()}</span></div>
                <div><span className="text-[#8a8a8a]">Avg PSF:</span> <span className="text-[#b4b4b4]">${avgPsf}</span></div>
                <div><span className="text-[#8a8a8a]">Avg Cap:</span> <span className="text-[#b4b4b4]">{avgCap ? `${avgCap}%` : "—"}</span></div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// COMPS VIEW
// ═════════════════════════════════════════════════════════════════════════════

export default function CompsView({ darkMode, onNavigate }) {
  const [view, setView] = useState("home"); // home | database | add-lease | add-sale | upload | awaiting-sale
  const [awaitingDeals, setAwaitingDeals] = useState([]);       // killed deals w/ market_cleared_at IS NULL
  const [awaitingClearing, setAwaitingClearing] = useState(null); // deal currently being recorded
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [leaseForm, setLeaseForm] = useState({ ...EMPTY_LEASE });
  // MSA dropdown options — used by both Add Lease and Add Sale forms below.
  // Includes any legacy free-text market values already present on the form
  // so a stale value stays selectable.
  const { msas: msaList } = useMsaList([leaseForm?.market].filter(Boolean));
  const [addedBy, setAddedBy] = useState("Daniel Manzi");
  const TEAM = ["Daniel Manzi", "Austin Orner", "Ignacio Portuondo"];
  const [saleForm, setSaleForm] = useState({ ...EMPTY_SALE });
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  // Database filters
  const [dbSearch, setDbSearch] = useState("");
  const [dbTypeFilter, setDbTypeFilter] = useState("all"); // all | lease | sale
  const [dbStatusFilter, setDbStatusFilter] = useState("all"); // all | active | expired
  const [dbMarketFilter, setDbMarketFilter] = useState("all");
  const [allCollapsed, setAllCollapsed] = useState(false);
  // Submarket grid: which one is currently expanded (null = none).
  const [expandedSubmarket, setExpandedSubmarket] = useState(null);
  // Which portfolio sale is open in a detail modal (null = none).
  const [expandedPortfolio, setExpandedPortfolio] = useState(null);
  // Drawer: a single comp opened in detail view (independent of submarket modal).
  const [expandedCompId, setExpandedCompId] = useState(null);
  // Tracking filter: "exclude" | "only" | "include"
  // Default excludes Tracking comps from aggregates so market avgs aren't muddied.
  const [trackingFilter, setTrackingFilter] = useState("exclude");
  // Mark-as-traded form state (when promoting a tracking comp to a real closed trade)
  const [markTradedForm, setMarkTradedForm] = useState(null); // { compId, sale_price, comp_date, buyer, cap_rate, notes }

  // ── Load comps ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadComps();
    loadAwaitingDeals();
  }, []);

  // Pulls every deal in a killed stage (Passed / Lost / Dead) that has
  // no market_cleared_at yet — i.e. "we bowed out, still don't know
  // what the market paid." Used by the Passed Deals Awaiting Sale view
  // in the Comps tab and also for the badge on the Comps home card.
  async function loadAwaitingDeals() {
    const { data, error } = await supabase
      .from("deals")
      .select("*")
      .in("deal_stage", ["Passed", "Lost", "Dead"])
      .is("market_cleared_at", null)
      .order("killed_at", { ascending: false })
      .limit(500);
    if (error) { console.warn("loadAwaitingDeals:", error.message); return; }
    setAwaitingDeals(data || []);
  }

  async function loadComps() {
    setLoading(true);
    // Supabase caps a single .select() at 1000 rows. Page through with
    // .range() until we get less than a full page back.
    const PAGE = 1000;
    let from = 0;
    let all = [];
    while (true) {
      const { data, error } = await supabase
        .from("comps")
        .select("*")
        .order("comp_date", { ascending: false })
        .range(from, from + PAGE - 1);
      if (error) { console.error("loadComps page error:", error); break; }
      if (!data || data.length === 0) break;
      all = all.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    // Update active/expired status
    const updated = all.map(c => ({
      ...c,
      status: isActive(c.type, c.comp_date) ? "active" : "expired",
    }));
    setComps(updated);
    setLoading(false);
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  // ── Navigate to Map view focused on this comp ──────────────────────────
  // Requires App.js to wire `onNavigate('map', { focusCompId })` through.
  function viewCompOnMap(comp) {
    if (!comp.latitude || !comp.longitude) {
      showToast("No coordinates for this comp — can't place on map.");
      return;
    }
    if (typeof onNavigate !== "function") {
      showToast("Map navigation isn't available in this view.");
      return;
    }
    onNavigate("map", { focusCompId: comp.id });
  }

  // ── Mark a "Tracking (Pipeline)" comp as traded (the trade we waited for) ──
  async function submitMarkTraded() {
    if (!markTradedForm) return;
    const { compId, sale_price, comp_date, buyer, cap_rate, notes } = markTradedForm;
    if (!sale_price || !comp_date) {
      showToast("Sale price and trade date are required.");
      return;
    }
    setSaving(true);
    try {
      const existing = comps.find(c => c.id === compId);
      const sf = existing?.building_sf;
      const pricePsf = sf ? Math.round(Number(sale_price) / Number(sf)) : null;
      const mergedNotes = [existing?.notes, notes ? `\n\n— Trade close (${comp_date}): ${notes}` : ""]
        .filter(Boolean).join("");
      const update = {
        sale_price: Number(sale_price),
        price_psf: pricePsf,
        comp_date,
        buyer: buyer || existing?.buyer || null,
        cap_rate: cap_rate ? Number(cap_rate) : existing?.cap_rate || null,
        status_detail: "Closed",
        status: isActive("sale", comp_date) ? "active" : "expired",
        notes: mergedNotes || null,
      };
      const { error } = await supabase.from("comps").update(update).eq("id", compId);
      if (error) throw error;
      showToast("Marked as traded — comp updated.");
      setMarkTradedForm(null);
      await loadComps();
    } catch (e) {
      showToast("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Save Lease Comp ─────────────────────────────────────────────────────
  async function saveLeaseComp() {
    if (!leaseForm.address || !leaseForm.tenant) { showToast("Address and Tenant are required."); return; }
    setSaving(true);
    try {
      // Check duplicate
      if (isDuplicateLease(comps, { ...leaseForm, type: "lease" })) {
        showToast("Duplicate lease comp — already exists.");
        setSaving(false);
        return;
      }
      // Geocode
      const fullAddr = [leaseForm.address, leaseForm.city, leaseForm.state].filter(Boolean).join(", ");
      const coords = await geocodeAddress(fullAddr);

      const row = {
        type: "lease",
        status: isActive("lease", leaseForm.comp_date) ? "active" : "expired",
        property_name: leaseForm.property_name || null,
        address: leaseForm.address,
        city: leaseForm.city || null,
        state: leaseForm.state || null,
        market: leaseForm.market || null,
        micro_market: leaseForm.micro_market || null,
        tenant: leaseForm.tenant,
        tenant_industry: leaseForm.tenant_industry || null,
        building_sf: leaseForm.building_sf ? Number(leaseForm.building_sf) : null,
        suite_sf: leaseForm.suite_sf ? Number(leaseForm.suite_sf) : null,
        lease_rate: leaseForm.lease_rate ? Number(leaseForm.lease_rate) : null,
        lease_type: leaseForm.lease_type || null,
        opex_psf: leaseForm.opex_psf === "" || leaseForm.opex_psf == null ? null : Number(leaseForm.opex_psf),
        comp_date: leaseForm.comp_date || null,
        landlord: leaseForm.landlord || null,
        notes: leaseForm.notes || null,
        source: leaseForm.source || null,
        loading_type: leaseForm.loading_type || null,
        door_type: leaseForm.door_type || null,
        year_built: leaseForm.year_built || null,
        clear_height: leaseForm.clear_height || null,
        added_by: addedBy,
        latitude: coords?.lat || null,
        longitude: coords?.lng || null,
      };

      const { error } = await supabase.from("comps").insert([row]);
      if (error) throw error;

      showToast("Lease comp saved!");
      setLeaseForm({ ...EMPTY_LEASE });
      await loadComps();
      setView("database");
    } catch (e) {
      showToast("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Save Sale Comp ──────────────────────────────────────────────────────
  async function saveSaleComp() {
    if (!saleForm.address) { showToast("Address is required."); return; }
    setSaving(true);
    try {
      if (isDuplicateSale(comps, { ...saleForm, type: "sale" })) {
        showToast("Duplicate sale comp — already exists.");
        setSaving(false);
        return;
      }
      const fullAddr = [saleForm.address, saleForm.city, saleForm.state].filter(Boolean).join(", ");
      const coords = await geocodeAddress(fullAddr);

      // Auto-calculate price_psf
      let pricePsf = saleForm.price_psf ? Number(saleForm.price_psf) : null;
      if (!pricePsf && saleForm.sale_price && saleForm.building_sf) {
        pricePsf = Math.round(Number(saleForm.sale_price) / Number(saleForm.building_sf));
      }

      const row = {
        type: "sale",
        status: isActive("sale", saleForm.comp_date) ? "active" : "expired",
        property_name: saleForm.property_name || null,
        address: saleForm.address,
        city: saleForm.city || null,
        state: saleForm.state || null,
        market: saleForm.market || null,
        micro_market: saleForm.micro_market || null,
        buyer: saleForm.buyer || null,
        seller: saleForm.seller || null,
        sale_price: saleForm.sale_price ? Number(saleForm.sale_price) : null,
        price_psf: pricePsf,
        building_sf: saleForm.building_sf ? Number(saleForm.building_sf) : null,
        cap_rate: saleForm.cap_rate ? Number(saleForm.cap_rate) : null,
        comp_date: saleForm.comp_date || null,
        portfolio_name: saleForm.portfolio_name || null,
        occupancy_at_closing: saleForm.occupancy_at_closing !== "" ? Number(saleForm.occupancy_at_closing) : null,
        walt_years: saleForm.walt_years !== "" ? Number(saleForm.walt_years) : null,
        year_built: saleForm.year_built || null,
        clear_height: saleForm.clear_height || null,
        office_finish_pct: saleForm.office_finish_pct !== "" ? Number(saleForm.office_finish_pct) : null,
        status_detail: saleForm.status_detail || null,
        notes: saleForm.notes || null,
        source: saleForm.source || null,
        added_by: addedBy,
        latitude: coords?.lat || null,
        longitude: coords?.lng || null,
      };

      const { error } = await supabase.from("comps").insert([row]);
      if (error) throw error;

      showToast("Sale comp saved!");
      setSaleForm({ ...EMPTY_SALE });
      await loadComps();
      setView("database");
    } catch (e) {
      showToast("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Upload Excel — AI-powered column mapping ────────────────────────────
  async function handleExcelUpload(file) {
    if (!file) return;
    setSaving(true);
    setUploadProgress("Reading Excel...");
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);

      if (rows.length === 0) { showToast("No rows found."); setSaving(false); setUploadProgress(null); return; }

      const columns = Object.keys(rows[0]);
      // Send first 5 rows + headers to Claude to figure out the mapping
      const sampleRows = rows.slice(0, 5).map(r => JSON.stringify(r)).join("\n");

      setUploadProgress(`Found ${rows.length} rows, ${columns.length} columns. AI analyzing format...`);

      const mappingPrompt = `You are a CRE data analyst. I have an Excel file with these columns and sample data. Map each column to the correct comp field.

COLUMNS: ${JSON.stringify(columns)}

SAMPLE DATA (first 5 rows):
${sampleRows}

Determine:
1. Is this LEASE comps or SALE comps (or mixed)?
2. Map each Excel column to the correct Proxy field.

Return ONLY valid JSON:
{
  "type": "lease" or "sale" or "mixed",
  "mapping": {
    "Excel Column Name": "proxy_field_name",
    ...
  }
}

Valid proxy_field_names for LEASE: property_name, address, city, state, market, tenant, tenant_industry, building_sf, suite_sf, lease_rate, lease_type, opex_psf, comp_date, landlord, notes, source, loading_type, door_type
Valid proxy_field_names for SALE: property_name, address, city, state, market, buyer, seller, sale_price, price_psf, building_sf, cap_rate, comp_date, portfolio_name, notes, source

Rules:
- Map as many columns as you can
- If a column doesn't fit any field, skip it
- "property_name" = building name or deal name
- "address" = street address
- Lease rates should map to lease_rate (annual $/SF)
- Dates map to comp_date (look for columns with year, quarter, date, commencement, execution, start, close)
- If dates are in "Q1 2024" or quarter format, still map them to comp_date
- "Submarket" or "Market" or "Area" should map to market
- SF/sqft/size/area maps to building_sf or suite_sf
- "Loading", "Loading Type", "Loading Position", "Loading Side" map to loading_type (values: Front, Rear, Front/Rear, Side)
- "Door Type", "Doors", "Loading Door Type" map to door_type (values: Grade, Street-Level, Dock, Dock-High)
- If you see both lease and sale data, use "mixed"`;

      // Retry up to 3 times in case of overload
      let aiResult;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          aiResult = await callClaudeWithMessages(
            mappingPrompt,
            [{ role: "user", content: "Map these columns." }],
            1024
          );
          break;
        } catch (e) {
          if (attempt < 2 && (e.message.includes("overload") || e.message.includes("529") || e.message.includes("rate"))) {
            setUploadProgress(`AI busy, retrying in ${(attempt + 1) * 3}s...`);
            await new Promise(r => setTimeout(r, (attempt + 1) * 3000));
          } else {
            throw e;
          }
        }
      }
      if (!aiResult) { showToast("AI unavailable. Try again in a moment."); setSaving(false); setUploadProgress(null); return; }

      let mapping;
      try {
        const cleaned = aiResult.replace(/```json?\s*/gi, "").replace(/```/g, "").trim();
        mapping = JSON.parse(cleaned);
      } catch (e) {
        showToast("AI couldn't analyze this format. Try PDF upload or manual entry.");
        setSaving(false); setUploadProgress(null); return;
      }

      const colMap = mapping.mapping || {};
      const detectedType = mapping.type || "lease";

      setUploadProgress(`AI mapped ${Object.keys(colMap).length} columns as ${detectedType}. Importing ${rows.length} rows...`);

      let imported = 0, skipped = 0;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const comp = { type: detectedType === "mixed" ? "lease" : detectedType };

        // Apply AI mapping
        for (const [excelCol, proxyField] of Object.entries(colMap)) {
          if (row[excelCol] !== undefined && row[excelCol] !== null && row[excelCol] !== "") {
            comp[proxyField] = row[excelCol];
          }
        }

        // If mixed type, detect per-row
        if (detectedType === "mixed") {
          if (comp.sale_price || comp.buyer || comp.price_psf) comp.type = "sale";
          else comp.type = "lease";
        }

        // Fallback: if no address, use property_name
        if (!comp.address && comp.property_name) comp.address = comp.property_name;
        // Skip if nothing to identify
        if (!comp.address && !comp.property_name && !comp.tenant && !comp.buyer) { skipped++; continue; }

        // Parse numerics
        ["building_sf", "suite_sf", "sale_price", "price_psf", "cap_rate", "lease_rate"].forEach(f => {
          if (comp[f]) comp[f] = parseFloat(String(comp[f]).replace(/[,$%]/g, "")) || null;
        });

        // Parse date — handle Excel serial numbers, Q1 2024 format, regular dates
        if (comp.comp_date) {
          let d;
          const raw = String(comp.comp_date).trim();
          // Excel serial number
          if (!isNaN(raw) && Number(raw) > 30000 && Number(raw) < 60000) {
            d = new Date((Number(raw) - 25569) * 86400000);
          }
          // Q1 2024, Q2 2025 format
          else if (/^Q[1-4]\s*\d{4}$/i.test(raw)) {
            const q = parseInt(raw.charAt(1));
            const year = parseInt(raw.slice(-4));
            const month = (q - 1) * 3; // Q1=Jan, Q2=Apr, Q3=Jul, Q4=Oct
            d = new Date(year, month, 1);
          }
          // "1Q24", "2Q25" format
          else if (/^[1-4]Q\s*\d{2,4}$/i.test(raw)) {
            const q = parseInt(raw.charAt(0));
            let year = parseInt(raw.replace(/[^0-9]/g, "").slice(1));
            if (year < 100) year += 2000;
            const month = (q - 1) * 3;
            d = new Date(year, month, 1);
          }
          // Regular date string
          else {
            d = new Date(raw);
          }
          comp.comp_date = (d && !isNaN(d.getTime()) && d.getFullYear() > 1980) ? d.toISOString().slice(0, 10) : null;
        }

        // Auto-calculate price_psf
        if (!comp.price_psf && comp.sale_price && comp.building_sf) {
          comp.price_psf = Math.round(comp.sale_price / comp.building_sf);
        }

        comp.status = isActive(comp.type, comp.comp_date) ? "active" : "expired";
        comp.source = comp.source || `Excel: ${file.name}`;
        comp.added_by = addedBy;

        // Geocode every comp
        const fullAddr = [comp.address, comp.city, comp.state].filter(Boolean).join(", ");
        if (fullAddr && fullAddr.length > 5) {
          try {
            const coords = await geocodeAddress(fullAddr);
            if (coords) { comp.latitude = coords.lat; comp.longitude = coords.lng; }
          } catch (e) { /* geocode failed, insert without coords */ }
        }
        const { error } = await supabase.from("comps").insert([comp]);
        if (!error) imported++;
        else { console.error("Insert error:", error); skipped++; }

        if ((i + 1) % 10 === 0 || i === rows.length - 1) {
          setUploadProgress(`Importing & geocoding... ${i + 1}/${rows.length} (${imported} saved)`);
        }
      }

      showToast(`Imported ${imported} comps, ${skipped} skipped.`);
      await loadComps();
      setView("database");
    } catch (e) {
      showToast("Upload error: " + e.message);
      console.error("Excel upload error:", e);
    } finally {
      setSaving(false);
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // ── Upload PDF ──────────────────────────────────────────────────────────
  async function handlePdfUpload(file) {
    if (!file) return;
    setSaving(true);
    setUploadProgress("Extracting text from PDF...");
    try {
      // Extract text from PDF using pdfjs
      const text = await extractTextFromFile(file);
      if (!text || text.length < 50) {
        showToast("Could not extract enough text from this PDF.");
        setSaving(false);
        setUploadProgress(null);
        return;
      }

      setUploadProgress("Analyzing document with AI...");

      const systemPrompt = `You are a commercial real estate data extraction specialist. Your job is to extract EVERY lease and EVERY sale from the document into a single combined list.

THERE ARE THREE TYPES OF SOURCES THAT EACH BECOME LEASE COMPS:

1. RENT ROLL (a table of in-place tenants for the SUBJECT PROPERTY of the OM).
   This is the most commonly missed source. A rent roll lists each tenant
   currently occupying the building. EVERY tenant row is a lease comp.
   - Headers to look for: "Rent Roll", "Stacking Plan", "Tenant Roster",
     "Lease Schedule", "Tenancy Schedule".
   - Row signals: Unit/Suite, Tenant, RSF/SF, Lease Start, Lease End, Rent
     ($/SF or annual), Lease Type, Escalations.
   - Skip rows where Tenant is "VACANT", "Vacant", or blank.
   - The address is NOT in each row — use the SUBJECT PROPERTY address from
     the OM cover page / executive summary for every rent-roll row.

2. LEASE COMPS section (a table of comparable leases at OTHER buildings).
   - Headers: "Lease Comparables", "Market Comps", "Comparable Leases",
     "Recent Leases".
   - Each row has its own address.

3. SALE COMPS section (comparable sales at other buildings).
   - Headers: "Sale Comparables", "Comparable Sales", "Recent Sales".

Return ONLY valid JSON in this exact format:
{
  "subject_property": {
    "address": "...",
    "city": "...",
    "state": "...",
    "market": "...",
    "property_name": "..."
  },
  "lease_comps": [
    {
      "source": "rent_roll" or "lease_comp",
      "property_name": "...",
      "address": "...",
      "city": "...",
      "state": "...",
      "market": "...",
      "micro_market": "..." or null,
      "tenant": "...",
      "tenant_industry": "...",
      "building_sf": null or number,
      "suite_sf": null or number,
      "lease_rate": null or number (annual $/SF),
      "lease_type": "NNN" or "Gross" or "Modified Gross",
      "opex_psf": null or number (annual operating expenses per SF - taxes + insurance + CAM. Only if stated; do not estimate),
      "comp_date": "YYYY-MM-DD" or null (use Lease Start date for rent-roll rows),
      "landlord": "...",
      "loading_type": "Front" or "Rear" or "Front/Rear" or "Side" or null,
      "door_type": "Grade" or "Street-Level" or "Dock" or "Dock-High" or null,
      "year_built": "..." or null (raw form, e.g. "1987", "1981-1986", "1977 / 2021"),
      "clear_height": "..." or null (raw form, e.g. "18'-24'", "22'"),
      "notes": "..."
    }
  ],
  "sale_comps": [
    {
      "property_name": "...",
      "address": "...",
      "city": "...",
      "state": "...",
      "market": "...",
      "micro_market": "..." or null,
      "buyer": "...",
      "seller": "...",
      "sale_price": null or number,
      "price_psf": null or number,
      "building_sf": null or number,
      "cap_rate": null or number (as percentage, e.g. 6.5),
      "comp_date": "YYYY-MM-DD" or null,
      "portfolio_name": "..." or null,
      "occupancy_at_closing": null or number (percentage 0-100, e.g. 94 not 0.94),
      "walt_years": null or number (weighted avg lease term remaining, in years, e.g. 3.8),
      "year_built": "..." or null (raw form, e.g. "1987", "1981-1986", "1977 / 2021"),
      "clear_height": "..." or null (raw form, e.g. "18'-24'", "22'"),
      "office_finish_pct": null or number (percentage 0-100),
      "status_detail": "..." or null ("Under Contract", "Closed", "Pending Trade", etc. — surface whatever the sheet says),
      "notes": "..."
    }
  ]
}

Rules:
- subject_property = the building the OM is selling. Find it on the cover or executive summary.
- For rent_roll rows: copy the SAME subject_property address/city/state/market into every row's address/city/state/market.
- For lease_comp rows (separate comp section): use that row's own address.
- Skip vacant rent-roll rows (Tenant = "VACANT" or blank).
- Extract ALL rent-roll rows regardless of Lease Start date. Every signed lease is a comp.
- If lease_rate looks like monthly $/SF (small number like 2-5), multiply by 12 to get annual.
- If lease_rate is total annual rent in dollars, divide by suite_sf to get $/SF.
- Cap rates as percentages (e.g. 6.5 not 0.065).
- Dates in YYYY-MM-DD format. Excel serial numbers (like 45261) need converting: 1900-01-01 + (n - 2) days.
- MARKET vs MICRO-MARKET (CRITICAL): if the sheet has BOTH a "Sub Market" / "Submarket" column AND a "Micro Market" / "Micromarket" column (NMRK broker comp sheets do), set "market" = the BROADER of the two ("Great Southwest / Arlington", "DFW Industrial") and "micro_market" = the NARROWER one ("Upper Great Southwest", "Plano", "East Hines North"). Do NOT conflate them. If only one tier exists, put it in "market" and leave "micro_market" null.
- NOTES (CRITICAL): the "notes" field MUST contain the FULL comments / remarks paragraph from the sheet, verbatim, including line breaks. Do NOT summarize, paraphrase, or shorten. Broker comments are load-bearing context (tenant rollover, rent bumps, occupancy %, office %, etc.) and the team relies on the raw text. If a row has no comments column, leave notes empty.
- year_built: keep the raw string form. "1987" stays "1987"; "1981-1986" stays "1981-1986"; "1977 / 2021" (year built / renovated) stays as-is.
- clear_height: keep the raw string form. "18'-24'" stays as-is; "22'" stays as-is.
- occupancy_at_closing / office_finish_pct: numbers as percentages 0-100 (NOT decimals). 94, not 0.94.
- walt_years: numeric years. "3.8 yrs" → 3.8.
- status_detail: copy the row's status verbatim if present ("Under Contract", "Closed", "Pending"), else null.
- Set "source" to "rent_roll" or "lease_comp" so we can tell them apart.
- Return empty arrays if a category has no entries.
- Do NOT include any text outside the JSON. No prose, no code fences.`;

      // OMs are routinely 50-100+ pages. The rent roll and comp pages live
      // anywhere in the document, so we need to send much more than 25k chars.
      // Claude's context easily handles 200k chars; cap there as a safety.
      const MAX_CHARS = 200000;
      const truncatedText = text.length > MAX_CHARS ? text.slice(0, MAX_CHARS) : text;
      if (text.length > MAX_CHARS) {
        console.warn(`OM text was ${text.length} chars; truncated to ${MAX_CHARS}.`);
      }
      // Bumped max_tokens to 16k. Claude Sonnet 4.5 supports up to 64k output;
      // a long rent roll + comp section can easily run 8k+, and the previous
      // 8k cap was truncating the JSON mid-array, causing parse failures.
      const result = await callClaudeWithMessages(
        systemPrompt,
        [{ role: "user", content: `Extract all comps from this document:\n\n${truncatedText}` }],
        16384
      );

      // Parse JSON response — Claude sometimes wraps with prose ("Here are
      // the comps:") even when told not to, so try to locate the {...} block.
      // We also try to repair truncated JSON (closing braces/brackets that
      // got cut off mid-response) by walking back to the last complete entry.
      let parsed;
      try {
        let cleaned = result.replace(/```json?\s*/gi, "").replace(/```/g, "").trim();
        const firstBrace = cleaned.indexOf("{");
        const lastBrace = cleaned.lastIndexOf("}");
        if (firstBrace >= 0 && lastBrace > firstBrace) {
          cleaned = cleaned.slice(firstBrace, lastBrace + 1);
        }
        try {
          parsed = JSON.parse(cleaned);
        } catch (firstErr) {
          // Likely truncated mid-array. Try to repair by walking back to the
          // last `},` (end of a complete array entry), trimming, and closing
          // any open arrays/objects so we recover everything that DID make it.
          console.warn("Initial JSON.parse failed, attempting truncation repair:", firstErr.message);
          let salvaged = cleaned;
          const lastEntryEnd = salvaged.lastIndexOf("},");
          if (lastEntryEnd > 0) {
            // Cut at the last complete entry, then close brackets that were left open
            salvaged = salvaged.slice(0, lastEntryEnd + 1); // through the '}'
            // Count unclosed [ and { that remain
            let openSquare = 0, openCurly = 0, inStr = false, esc = false;
            for (const ch of salvaged) {
              if (esc) { esc = false; continue; }
              if (ch === "\\") { esc = true; continue; }
              if (ch === '"') { inStr = !inStr; continue; }
              if (inStr) continue;
              if (ch === "[") openSquare++;
              else if (ch === "]") openSquare--;
              else if (ch === "{") openCurly++;
              else if (ch === "}") openCurly--;
            }
            salvaged += "]".repeat(Math.max(0, openSquare)) + "}".repeat(Math.max(0, openCurly));
            try {
              parsed = JSON.parse(salvaged);
              console.warn("Recovered partial JSON. Some comps near the end of the document may have been dropped.");
              showToast("Note: response was truncated. Imported what we could parse.", 6000);
            } catch (secondErr) {
              throw firstErr; // re-throw original
            }
          } else {
            throw firstErr;
          }
        }
      } catch (e) {
        console.error("Comp parse failure. Claude raw response:", result);
        showToast(`AI parse error. Check console for raw output. (${e.message})`);
        setSaving(false);
        setUploadProgress(null);
        return;
      }

      const leaseComps = parsed.lease_comps || [];
      const saleComps = parsed.sale_comps || [];
      const total = leaseComps.length + saleComps.length;

      if (total === 0) {
        showToast("No comps found in this document.");
        setSaving(false);
        setUploadProgress(null);
        return;
      }

      setUploadProgress(`Found ${leaseComps.length} lease + ${saleComps.length} sale comps. Saving...`);

      let imported = 0, skipped = 0;

      // Save lease comps
      for (const lc of leaseComps) {
        if (!lc.address) { skipped++; continue; }
        if (isDuplicateLease(comps, { ...lc, type: "lease" })) { skipped++; continue; }
        const fullAddr = [lc.address, lc.city, lc.state].filter(Boolean).join(", ");
        const coords = await geocodeAddress(fullAddr);
        const row = {
          type: "lease",
          status: isActive("lease", lc.comp_date) ? "active" : "expired",
          property_name: lc.property_name || null,
          address: lc.address,
          city: lc.city || null,
          state: lc.state || null,
          market: lc.market || null,
          tenant: lc.tenant || null,
          tenant_industry: lc.tenant_industry || null,
          building_sf: lc.building_sf || null,
          suite_sf: lc.suite_sf || null,
          lease_rate: lc.lease_rate || null,
          lease_type: lc.lease_type || null,
          opex_psf: lc.opex_psf ?? null,
          comp_date: lc.comp_date || null,
          landlord: lc.landlord || null,
          loading_type: lc.loading_type || null,
          door_type: lc.door_type || null,
          year_built: lc.year_built || null,
          clear_height: lc.clear_height || null,
          micro_market: lc.micro_market || null,
          notes: lc.notes || null,
          source: lc.source === "rent_roll"
            ? `Rent Roll: ${file.name}`
            : `PDF: ${file.name}`,
          added_by: addedBy,
          latitude: coords?.lat || null,
          longitude: coords?.lng || null,
        };
        const { error } = await supabase.from("comps").insert([row]);
        if (!error) imported++;
        else skipped++;
      }

      // Save sale comps
      for (const sc of saleComps) {
        if (!sc.address) { skipped++; continue; }
        if (isDuplicateSale(comps, { ...sc, type: "sale" })) { skipped++; continue; }
        const fullAddr = [sc.address, sc.city, sc.state].filter(Boolean).join(", ");
        const coords = await geocodeAddress(fullAddr);
        let pricePsf = sc.price_psf || null;
        if (!pricePsf && sc.sale_price && sc.building_sf) {
          pricePsf = Math.round(sc.sale_price / sc.building_sf);
        }
        const row = {
          type: "sale",
          status: isActive("sale", sc.comp_date) ? "active" : "expired",
          property_name: sc.property_name || null,
          address: sc.address,
          city: sc.city || null,
          state: sc.state || null,
          market: sc.market || null,
          buyer: sc.buyer || null,
          seller: sc.seller || null,
          sale_price: sc.sale_price || null,
          price_psf: pricePsf,
          building_sf: sc.building_sf || null,
          cap_rate: sc.cap_rate || null,
          comp_date: sc.comp_date || null,
          portfolio_name: sc.portfolio_name || null,
          occupancy_at_closing: sc.occupancy_at_closing ?? null,
          walt_years: sc.walt_years ?? null,
          year_built: sc.year_built || null,
          clear_height: sc.clear_height || null,
          office_finish_pct: sc.office_finish_pct ?? null,
          micro_market: sc.micro_market || null,
          status_detail: sc.status_detail || null,
          notes: sc.notes || null,
          source: `PDF: ${file.name}`,
          added_by: addedBy,
          latitude: coords?.lat || null,
          longitude: coords?.lng || null,
        };
        const { error } = await supabase.from("comps").insert([row]);
        if (!error) imported++;
        else skipped++;
      }

      showToast(`Imported ${imported} comps from PDF (${skipped} skipped/duplicates).`);
      await loadComps();
      setView("database");
    } catch (e) {
      showToast("PDF upload error: " + e.message);
    } finally {
      setSaving(false);
      setUploadProgress(null);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  }

  // ── Computed ─────────────────────────────────────────────────────────────
  const isMissingData = (c) => !c.address || !c.address.trim() || (!c.latitude && !c.longitude);
  const missingData = comps.filter(isMissingData);
  const validComps = comps.filter(c => !isMissingData(c));
  const activeLeases = validComps.filter(c => c.type === "lease" && c.status === "active");
  const activeSales = validComps.filter(c => c.type === "sale" && c.status === "active");
  const expiredLeases = validComps.filter(c => c.type === "lease" && c.status === "expired");
  const expiredSales = validComps.filter(c => c.type === "sale" && c.status === "expired");
  // Portfolio aggregates exclude Tracking comps so price/cap math reflects real
  // closed trades only — a $0 placeholder would otherwise drag down the average.
  const portfolios = detectPortfolios(
    activeSales.concat(expiredSales).filter(c => !isTrackingComp(c))
  );

  // ── Styles ──────────────────────────────────────────────────────────────
  const card = "bg-[#2c2c2c] border border-[rgba(58,58,58,0.5)] rounded-xl p-6 hover:border-pp-red/40 transition-all cursor-pointer";
  const sectionTitle = "text-lg font-bold text-white font-montserrat mb-4";
  const badge = (color) => `text-xs font-semibold px-2 py-0.5 rounded-full ${color}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="flex gap-1.5">
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-2 h-2 bg-pp-red rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    );
  }

  // ═══ HOME ═══════════════════════════════════════════════════════════════
  if (view === "home") {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        {toast && <div className="fixed top-4 right-4 z-50 bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)] px-4 py-2 rounded-lg text-sm shadow-lg">{toast}</div>}

        <h2 className="text-2xl font-bold text-white font-montserrat mb-6">Comps</h2>

        <div className="grid grid-cols-3 gap-6 mb-8">
          <div onClick={() => setView("add-home")} className={card}>
            <div className="text-3xl mb-3">📝</div>
            <h3 className="text-white font-bold text-lg mb-1">Add a Comp</h3>
            <p className="text-[#8a8a8a] text-sm">Upload PDF, Excel, or enter manually</p>
          </div>
          <div onClick={() => setView("database")} className={card}>
            <div className="text-3xl mb-3">📊</div>
            <h3 className="text-white font-bold text-lg mb-1">Comp Database</h3>
            <p className="text-[#8a8a8a] text-sm">
              {comps.length} comps — {activeLeases.length} active leases, {activeSales.length} active sales
            </p>
          </div>
          <div onClick={() => setView("awaiting-sale")} className={card}>
            <div className="text-3xl mb-3">⏳</div>
            <h3 className="text-white font-bold text-lg mb-1">Passed Deals Awaiting Sale</h3>
            <p className="text-[#8a8a8a] text-sm">
              {awaitingDeals.length} killed deal{awaitingDeals.length === 1 ? "" : "s"} pending market clearing price
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ═══ AWAITING SALE ══════════════════════════════════════════════════════
  if (view === "awaiting-sale") {
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        {toast && <div className="fixed top-4 right-4 z-50 bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)] px-4 py-2 rounded-lg text-sm shadow-lg">{toast}</div>}

        <button onClick={() => setView("home")} className="text-[#8a8a8a] hover:text-white text-sm mb-4 flex items-center gap-1">← Back</button>
        <h2 className="text-2xl font-bold text-white font-montserrat mb-1">Passed Deals Awaiting Sale</h2>
        <p className="text-sm text-[#8a8a8a] mb-6">
          Deals we passed on, lost, or that went dead — waiting for the market to clear so we can compare our bid to where they actually traded.
        </p>

        <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.5)] rounded-xl overflow-hidden">
          <div className="grid grid-cols-[minmax(240px,1fr)_140px_110px_100px_100px_140px_90px] gap-3 px-4 py-3 border-b border-[rgba(58,58,58,0.5)] text-[10px] font-bold text-[#8a8a8a] uppercase tracking-wider">
            <div>Address</div>
            <div>MSA</div>
            <div>Stage</div>
            <div className="text-right">Our Bid $/SF</div>
            <div>Killed</div>
            <div>Reason</div>
            <div className="text-right">Action</div>
          </div>
          {awaitingDeals.length === 0 ? (
            <p className="text-sm text-[#8a8a8a] p-6 text-center">All caught up — every killed deal has a market clearing entry.</p>
          ) : awaitingDeals.map(d => {
            let addrs;
            try { addrs = JSON.parse(d.address); if (!Array.isArray(addrs)) addrs = [d.address]; } catch { addrs = [d.address]; }
            return (
              <div key={d.id}
                className="grid grid-cols-[minmax(240px,1fr)_140px_110px_100px_100px_140px_90px] gap-3 px-4 py-2 border-b border-[rgba(58,58,58,0.3)] items-center text-sm hover:bg-[rgba(44,44,44,0.4)] transition-colors">
                <div className="min-w-0">
                  <div className="text-[#f4f4f4] truncate">{addrs?.[0] || "—"}</div>
                  {d.killed_narrative && <div className="text-[10px] text-[#8a8a8a] truncate">{d.killed_narrative}</div>}
                </div>
                <div className="text-[#b4b4b4] text-xs truncate">{(d.msas || []).join(", ") || "—"}</div>
                <div className="text-[#b4b4b4] text-xs">{d.deal_stage}</div>
                <div className="text-[#b4b4b4] text-xs text-right">{d._ourBidPsf ? `$${d._ourBidPsf}` : "—"}</div>
                <div className="text-[#8a8a8a] text-xs">{d.killed_at ? new Date(d.killed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "—"}</div>
                <div className="text-[#8a8a8a] text-xs truncate">{d.killed_reason || "—"}</div>
                <div className="text-right">
                  <button onClick={() => setAwaitingClearing(d)}
                    className="text-[11px] px-2 py-1 rounded bg-pp-red hover:bg-pp-red-light text-white font-semibold">
                    Record sale
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {awaitingClearing && (
          <AwaitingSaleModal
            deal={awaitingClearing}
            onClose={() => setAwaitingClearing(null)}
            onSaved={async () => {
              setAwaitingClearing(null);
              await loadAwaitingDeals();
              showToast("Market clearing price recorded.");
            }}
          />
        )}
      </div>
    );
  }

  // ═══ ADD HOME ═══════════════════════════════════════════════════════════
  if (view === "add-home") {
    async function processFile(file) {
      const name = file.name.toLowerCase();
      if (name.endsWith(".pdf")) await handlePdfUpload(file);
      else if (name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv")) await handleExcelUpload(file);
      else showToast("Unsupported file: " + file.name);
    }
    async function handleDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length || saving) return;
      setUploadProgress(`Processing ${files.length} file${files.length > 1 ? "s" : ""}...`);
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(`Processing file ${i + 1} of ${files.length}: ${files[i].name}`);
        await processFile(files[i]);
      }
      setUploadProgress(null);
    }
    async function handleFileSelect(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      if (files.length === 1) {
        await processFile(files[0]);
      } else {
        setUploadProgress(`Processing ${files.length} files...`);
        for (let i = 0; i < files.length; i++) {
          setUploadProgress(`Processing file ${i + 1} of ${files.length}: ${files[i].name}`);
          await processFile(files[i]);
        }
        setUploadProgress(null);
      }
    }
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        {toast && <div className="fixed top-4 right-4 z-50 bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)] px-4 py-2 rounded-lg text-sm shadow-lg">{toast}</div>}

        <button onClick={() => setView("home")} className="text-[#8a8a8a] hover:text-white text-sm mb-4 flex items-center gap-1">← Back</button>
        <h2 className="text-2xl font-bold text-white font-montserrat mb-6">Add a Comp</h2>

        {/* Upload progress */}
        {uploadProgress && (
          <div className="mb-6 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-4 py-3 text-sm text-[#b4b4b4] flex items-center gap-2">
            <span className="animate-pulse text-pp-red">●</span>
            {uploadProgress}
          </div>
        )}

        {/* Added by selector */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-[#8a8a8a] text-sm font-semibold">Adding as:</span>
          <select value={addedBy} onChange={e => setAddedBy(e.target.value)}
            className="bg-[#2c2c2c] border border-[#3a3a3a] text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-pp-red">
            {TEAM.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Drag & Drop Upload Zone */}
        <div
          onClick={() => !saving && fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all mb-6 ${saving ? "opacity-50 cursor-not-allowed border-[#4a4a4a]" : "border-[#4a4a4a] hover:border-pp-red/60 hover:bg-[rgba(44,44,44,0.5)]"}`}
        >
          <div className="text-4xl mb-3">📁</div>
          <p className="text-white font-semibold font-montserrat text-lg mb-1">
            {saving ? "Processing..." : "Drop files here or click to upload (multiple files supported)"}
          </p>
          <p className="text-[#8a8a8a] text-sm mb-3">
            PDF (OMs, rent rolls, broker comp sheets) or Excel (lease/sale comp spreadsheets)
          </p>
          <p className="text-[#8a8a8a] text-xs">
            AI automatically extracts and categorizes comps from your documents
          </p>
        </div>

        {/* Manual Entry */}
        <div className="mb-2">
          <p className="text-[#8a8a8a] text-xs uppercase tracking-wider font-semibold mb-3">Or enter manually</p>
          <div className="flex gap-3">
            <button onClick={() => setView("add-lease")} className="flex-1 bg-[#2c2c2c] border border-[rgba(58,58,58,0.5)] rounded-xl p-4 hover:border-pp-red/40 transition-all text-left">
              <span className="text-white font-semibold text-sm">Add Lease Comp</span>
              <p className="text-[#8a8a8a] text-xs mt-0.5">Tenant, rate, SF, lease date</p>
            </button>
            <button onClick={() => setView("add-sale")} className="flex-1 bg-[#2c2c2c] border border-[rgba(58,58,58,0.5)] rounded-xl p-4 hover:border-pp-red/40 transition-all text-left">
              <span className="text-white font-semibold text-sm">Add Sale Comp</span>
              <p className="text-[#8a8a8a] text-xs mt-0.5">Price, PSF, cap rate, buyer</p>
            </button>
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept=".pdf,.xlsx,.xls,.csv" multiple className="hidden"
          onChange={handleFileSelect} />
      </div>
    );
  }

  // ═══ ADD LEASE COMP ════════════════════════════════════════════════════
  if (view === "add-lease") {
    const set = (k, v) => setLeaseForm(f => ({ ...f, [k]: v }));
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        {toast && <div className="fixed top-4 right-4 z-50 bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)] px-4 py-2 rounded-lg text-sm shadow-lg">{toast}</div>}
        <button onClick={() => setView("add-home")} className="text-[#8a8a8a] hover:text-white text-sm mb-4 flex items-center gap-1">← Back</button>
        <h2 className="text-2xl font-bold text-white font-montserrat mb-6">Add Lease Comp</h2>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Property Name"><input value={leaseForm.property_name} onChange={e => set("property_name", e.target.value)} className={inputCls()} /></Field>
            <Field label="Market">
              <select value={leaseForm.market} onChange={e => set("market", e.target.value)} className={inputCls()}>
                {msaList.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Address" required><input value={leaseForm.address} onChange={e => set("address", e.target.value)} className={inputCls()} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="City"><input value={leaseForm.city} onChange={e => set("city", e.target.value)} className={inputCls()} /></Field>
            <Field label="State"><input value={leaseForm.state} onChange={e => set("state", e.target.value)} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tenant" required><input value={leaseForm.tenant} onChange={e => set("tenant", e.target.value)} className={inputCls()} /></Field>
            <Field label="Tenant Industry"><input value={leaseForm.tenant_industry} onChange={e => set("tenant_industry", e.target.value)} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Building SF"><input type="number" value={leaseForm.building_sf} onChange={e => set("building_sf", e.target.value)} className={inputCls()} /></Field>
            <Field label="Suite SF"><input type="number" value={leaseForm.suite_sf} onChange={e => set("suite_sf", e.target.value)} className={inputCls()} /></Field>
            <Field label="Lease Rate ($/SF)"><input type="number" step="0.01" value={leaseForm.lease_rate} onChange={e => set("lease_rate", e.target.value)} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Lease Type">
              <select value={leaseForm.lease_type} onChange={e => set("lease_type", e.target.value)} className={inputCls()}>
                <option value="NNN">NNN</option>
                <option value="Gross">Gross</option>
                <option value="Modified Gross">Modified Gross</option>
                <option value="Other">Other</option>
              </select>
            </Field>
            <Field label="OPEX ($/SF/yr)">
              <input type="number" step="0.01" value={leaseForm.opex_psf}
                onChange={e => set("opex_psf", e.target.value)}
                placeholder={leaseForm.lease_type === "NNN" ? "tenant-paid" : "landlord-paid"}
                className={inputCls()} />
            </Field>
            <Field label="Lease Date"><input type="date" value={leaseForm.comp_date} onChange={e => set("comp_date", e.target.value)} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Landlord"><input value={leaseForm.landlord} onChange={e => set("landlord", e.target.value)} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Loading Type">
              <select value={leaseForm.loading_type || ""} onChange={e => set("loading_type", e.target.value)} className={inputCls()}>
                <option value="">—</option>
                <option value="Front">Front</option>
                <option value="Rear">Rear</option>
                <option value="Front/Rear">Front/Rear</option>
                <option value="Side">Side</option>
              </select>
            </Field>
            <Field label="Door Type">
              <select value={leaseForm.door_type || ""} onChange={e => set("door_type", e.target.value)} className={inputCls()}>
                <option value="">—</option>
                <option value="Grade">Grade</option>
                <option value="Street-Level">Street-Level</option>
                <option value="Dock">Dock</option>
                <option value="Dock-High">Dock-High</option>
              </select>
            </Field>
          </div>
          <Field label="Source"><input value={leaseForm.source} onChange={e => set("source", e.target.value)} placeholder="e.g. CBRE, CoStar, Broker" className={inputCls()} /></Field>
          <CollapsibleSection title="Property details (optional)">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Micro Market"><input value={leaseForm.micro_market} onChange={e => set("micro_market", e.target.value)} placeholder="e.g. Upper Great Southwest" className={inputCls()} /></Field>
              <Field label="Year Built"><input value={leaseForm.year_built} onChange={e => set("year_built", e.target.value)} placeholder="e.g. 1987 or 1981-1986" className={inputCls()} /></Field>
              <Field label="Clear Height"><input value={leaseForm.clear_height} onChange={e => set("clear_height", e.target.value)} placeholder={`e.g. 18'-24'`} className={inputCls()} /></Field>
            </div>
          </CollapsibleSection>
          <Field label="Notes"><textarea value={leaseForm.notes} onChange={e => set("notes", e.target.value)} rows={3} className={inputCls() + " resize-none"} /></Field>
          <div className="flex gap-3">
            <button onClick={saveLeaseComp} disabled={saving} className="bg-pp-red hover:bg-pp-red-light disabled:opacity-50 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors" style={{ color: '#fff' }}>
              {saving ? "Saving..." : "Save Lease Comp"}
            </button>
            <button onClick={() => setView("add-home")} className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#f4f4f4] px-4 py-2.5 rounded-lg transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ ADD SALE COMP ═════════════════════════════════════════════════════
  if (view === "add-sale") {
    const set = (k, v) => setSaleForm(f => ({ ...f, [k]: v }));
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        {toast && <div className="fixed top-4 right-4 z-50 bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)] px-4 py-2 rounded-lg text-sm shadow-lg">{toast}</div>}
        <button onClick={() => setView("add-home")} className="text-[#8a8a8a] hover:text-white text-sm mb-4 flex items-center gap-1">← Back</button>
        <h2 className="text-2xl font-bold text-white font-montserrat mb-6">Add Sale Comp</h2>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Property Name"><input value={saleForm.property_name} onChange={e => set("property_name", e.target.value)} className={inputCls()} /></Field>
            <Field label="Market">
              <select value={saleForm.market} onChange={e => set("market", e.target.value)} className={inputCls()}>
                {msaList.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Address" required><input value={saleForm.address} onChange={e => set("address", e.target.value)} className={inputCls()} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="City"><input value={saleForm.city} onChange={e => set("city", e.target.value)} className={inputCls()} /></Field>
            <Field label="State"><input value={saleForm.state} onChange={e => set("state", e.target.value)} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Buyer"><input value={saleForm.buyer} onChange={e => set("buyer", e.target.value)} className={inputCls()} /></Field>
            <Field label="Seller"><input value={saleForm.seller} onChange={e => set("seller", e.target.value)} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Sale Price ($)"><input type="number" value={saleForm.sale_price} onChange={e => set("sale_price", e.target.value)} className={inputCls()} /></Field>
            <Field label="Price PSF ($)"><input type="number" step="0.01" value={saleForm.price_psf} onChange={e => set("price_psf", e.target.value)} className={inputCls()} /></Field>
            <Field label="Building SF"><input type="number" value={saleForm.building_sf} onChange={e => set("building_sf", e.target.value)} className={inputCls()} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Cap Rate (%)"><input type="number" step="0.01" value={saleForm.cap_rate} onChange={e => set("cap_rate", e.target.value)} className={inputCls()} /></Field>
            <Field label="Sale Date"><input type="date" value={saleForm.comp_date} onChange={e => set("comp_date", e.target.value)} className={inputCls()} /></Field>
            <Field label="Portfolio Name"><input value={saleForm.portfolio_name} onChange={e => set("portfolio_name", e.target.value)} placeholder="Optional" className={inputCls()} /></Field>
          </div>
          <Field label="Source"><input value={saleForm.source} onChange={e => set("source", e.target.value)} placeholder="e.g. CBRE, CoStar, Broker" className={inputCls()} /></Field>
          <CollapsibleSection title="Property details (optional)">
            <div className="grid grid-cols-3 gap-4">
              <Field label="Micro Market"><input value={saleForm.micro_market} onChange={e => set("micro_market", e.target.value)} placeholder="e.g. Upper Great Southwest" className={inputCls()} /></Field>
              <Field label="Status Detail"><input value={saleForm.status_detail} onChange={e => set("status_detail", e.target.value)} placeholder="e.g. Closed, Under Contract" className={inputCls()} /></Field>
              <Field label="Year Built"><input value={saleForm.year_built} onChange={e => set("year_built", e.target.value)} placeholder="e.g. 1987 or 1977 / 2021" className={inputCls()} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <Field label="Clear Height"><input value={saleForm.clear_height} onChange={e => set("clear_height", e.target.value)} placeholder={`e.g. 18'-24'`} className={inputCls()} /></Field>
              <Field label="Occupancy at Closing (%)"><input type="number" step="0.1" value={saleForm.occupancy_at_closing} onChange={e => set("occupancy_at_closing", e.target.value)} placeholder="e.g. 94" className={inputCls()} /></Field>
              <Field label="WALT (years)"><input type="number" step="0.1" value={saleForm.walt_years} onChange={e => set("walt_years", e.target.value)} placeholder="e.g. 3.8" className={inputCls()} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <Field label="Office Finish (%)"><input type="number" step="0.1" value={saleForm.office_finish_pct} onChange={e => set("office_finish_pct", e.target.value)} placeholder="e.g. 12" className={inputCls()} /></Field>
            </div>
          </CollapsibleSection>
          <Field label="Notes"><textarea value={saleForm.notes} onChange={e => set("notes", e.target.value)} rows={3} className={inputCls() + " resize-none"} /></Field>
          <div className="flex gap-3">
            <button onClick={saveSaleComp} disabled={saving} className="bg-pp-red hover:bg-pp-red-light disabled:opacity-50 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors" style={{ color: '#fff' }}>
              {saving ? "Saving..." : "Save Sale Comp"}
            </button>
            <button onClick={() => setView("add-home")} className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#f4f4f4] px-4 py-2.5 rounded-lg transition-colors">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ COMP DATABASE ═════════════════════════════════════════════════════
  if (view === "database") {
    const leaseTypeBadge = (lt) => {
      const colors = { NNN: "bg-[rgba(138,138,138,0.22)] text-[#b4b4b4]", "Modified Gross": "bg-[rgba(138,138,138,0.22)] text-[#b4b4b4]", MG: "bg-[rgba(138,138,138,0.22)] text-[#b4b4b4]", Gross: "bg-[rgba(79,157,118,0.2)] text-[#7cbb99]", BY: "bg-[rgba(233,53,41,0.2)] text-[#f04f44]" };
      if (!lt) return null;
      const key = lt.trim();
      return <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${colors[key] || "bg-[#4a4a4a] text-[#b4b4b4]"}`}>{key}</span>;
    };

    const fmtRate = (r) => r != null ? `$${Number(r).toFixed(2)}` : "—";

    const fmtCompDate = (d) => {
      if (!d) return "—";
      const date = new Date(d + "T00:00:00");
      if (isNaN(date.getTime())) return d;
      const q = Math.ceil((date.getMonth() + 1) / 3);
      return `Q${q} ${date.getFullYear()}`;
    };

    const CompCard = ({ comp }) => {
      const tracking = isTrackingComp(comp);
      return (
      <div
        onClick={() => setExpandedCompId(comp.id)}
        className={`bg-[#2c2c2c] rounded-lg p-4 hover:bg-[rgba(58,58,58,0.5)] transition-colors cursor-pointer border ${tracking ? "border-[rgba(233,53,41,0.4)]" : "border-transparent"}`}
      >
        <div className="flex items-start justify-between mb-1 gap-2">
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">{comp.property_name || comp.address}</p>
            {comp.property_name && <p className="text-[#8a8a8a] text-xs truncate">{comp.address}{comp.city ? `, ${comp.city}` : ""}</p>}
            <p className="text-pp-red text-xs font-medium mt-0.5 truncate">
              {comp.market || "—"}
              {comp.micro_market && <span className="text-[#8a8a8a]"> · {comp.micro_market}</span>}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {tracking && <span className={badge("bg-[rgba(233,53,41,0.25)] text-[#f04f44] border border-[rgba(233,53,41,0.4)]")}>Tracking</span>}
            {comp.type === "lease" && leaseTypeBadge(comp.lease_type)}
            <span className={badge(comp.type === "lease" ? "bg-[rgba(138,138,138,0.22)] text-[#b4b4b4]" : "bg-[rgba(233,53,41,0.2)] text-[#f04f44]")}>
              {comp.type === "lease" ? "Lease" : "Sale"}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 text-xs mt-2">
          {comp.type === "lease" ? (
            <>
              <div><span className="text-[#8a8a8a]">Tenant:</span> <span className="text-[#b4b4b4]">{comp.tenant || "—"}</span></div>
              <div><span className="text-[#8a8a8a]">Rate:</span> <span className="text-[#b4b4b4]">{fmtRate(comp.lease_rate)}/SF</span></div>
              <div><span className="text-[#8a8a8a]">SF:</span> <span className="text-[#b4b4b4]">{comp.suite_sf ? Number(comp.suite_sf).toLocaleString() : comp.building_sf ? Number(comp.building_sf).toLocaleString() : "—"}</span></div>
              <div><span className="text-[#8a8a8a]">Date:</span> <span className="text-[#b4b4b4]">{fmtCompDate(comp.comp_date)}</span></div>
            </>
          ) : (
            <>
              <div><span className="text-[#8a8a8a]">Price:</span> <span className="text-[#b4b4b4]">{comp.sale_price ? `$${Number(comp.sale_price).toLocaleString()}` : "—"}</span></div>
              <div><span className="text-[#8a8a8a]">PSF:</span> <span className="text-[#b4b4b4]">{comp.price_psf ? `$${Number(comp.price_psf).toFixed(2)}` : "—"}</span></div>
              <div><span className="text-[#8a8a8a]">Cap:</span> <span className="text-[#b4b4b4]">{comp.cap_rate ? `${Number(comp.cap_rate).toFixed(2)}%` : "—"}</span></div>
              <div><span className="text-[#8a8a8a]">Date:</span> <span className="text-[#b4b4b4]">{fmtCompDate(comp.comp_date)}</span></div>
            </>
          )}
        </div>
        {/* Sale comp story row: Seller → Buyer (only render if at least one is present) */}
        {comp.type === "sale" && (comp.seller || comp.buyer) && (
          <div className="mt-2 text-xs text-[#b4b4b4] truncate">
            <span className="text-[#8a8a8a]">Trade: </span>
            <span>{comp.seller || "—"}</span>
            <span className="text-[#8a8a8a] mx-1.5">→</span>
            <span>{comp.buyer || "—"}</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-2 text-xs text-[#8a8a8a]">
          <span>{comp.added_by ? `Added by ${comp.added_by}` : "Added by PPUSA"}</span>
          <button
            onClick={(e) => { e.stopPropagation(); viewCompOnMap(comp); }}
            className="flex items-center gap-1 text-[#8a8a8a] hover:text-pp-red transition-colors"
            title="View on Map"
          >
            <MapPinIcon /> Map
          </button>
        </div>
      </div>
      );
    };

    const Section = ({ title, items, color, defaultOpen = true }) => {
      const [open, setOpen] = useState(defaultOpen);
      const [filter, setFilter] = useState("");
      const [showCount, setShowCount] = useState(20);

      // Sort by market then address
      const sorted = [...items].sort((a, b) => {
        const mA = (a.market || "zzz").toLowerCase();
        const mB = (b.market || "zzz").toLowerCase();
        if (mA !== mB) return mA.localeCompare(mB);
        return (a.address || "").toLowerCase().localeCompare((b.address || "").toLowerCase());
      });

      const filtered = filter.trim()
        ? sorted.filter(c => {
            const q = filter.toLowerCase();
            return [c.address, c.city, c.tenant, c.buyer, c.seller, c.market, c.property_name, c.landlord]
              .filter(Boolean).join(" ").toLowerCase().includes(q);
          })
        : sorted;

      const visible = filtered.slice(0, showCount);
      const hasMore = filtered.length > showCount;

      return (
        <div className="mb-8">
          <button onClick={() => setOpen(!open)} className="flex items-center gap-3 mb-3 w-full text-left">
            <svg className={`w-4 h-4 text-[#8a8a8a] transition-transform ${open ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            <h3 className={sectionTitle + " mb-0"}>{title}</h3>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${color}`}>{items.length}</span>
          </button>
          {open && (
            <>
              {items.length > 5 && (
                <div className="mb-3">
                  <input
                    value={filter} onChange={e => { setFilter(e.target.value); setShowCount(20); }}
                    placeholder="Filter by address, tenant, buyer, market..."
                    className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#8a8a8a] w-full max-w-md focus:outline-none focus:border-pp-red"
                  />
                  {filter && <span className="text-xs text-[#8a8a8a] ml-2">{filtered.length} of {items.length}</span>}
                </div>
              )}
              {filtered.length === 0 ? (
                <p className="text-[#8a8a8a] text-sm">{filter ? "No comps match your filter." : "No comps in this category."}</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {visible.map(c => <CompCard key={c.id} comp={c} />)}
                  </div>
                  {hasMore && (
                    <button onClick={() => setShowCount(s => s + 20)} className="mt-3 text-pp-red hover:text-pp-red-light text-sm font-semibold transition-colors">
                      Show more ({filtered.length - showCount} remaining)
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      );
    };

    // Get unique markets for filter dropdown
    const uniqueMarkets = [...new Set(validComps.map(c => c.market).filter(Boolean))].sort();

    // Apply global filters to validComps
    const filteredComps = validComps.filter(c => {
      // Tracking filter — default "exclude" hides Tracking comps from the
      // browse view so they don't muddy market averages alongside real trades.
      // "only" shows just the tracking watch-list. "include" shows everything.
      const tracking = isTrackingComp(c);
      if (trackingFilter === "exclude" && tracking) return false;
      if (trackingFilter === "only" && !tracking) return false;
      if (dbTypeFilter !== "all" && c.type !== dbTypeFilter) return false;
      if (dbStatusFilter !== "all" && c.status !== dbStatusFilter) return false;
      if (dbMarketFilter !== "all" && c.market !== dbMarketFilter) return false;
      if (dbSearch.trim()) {
        const q = dbSearch.toLowerCase();
        const hay = [c.address, c.city, c.tenant, c.buyer, c.seller, c.market, c.micro_market, c.property_name, c.landlord, c.notes].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const hasActiveFilters = dbTypeFilter !== "all" || dbStatusFilter !== "all" || dbMarketFilter !== "all" || trackingFilter !== "exclude" || dbSearch.trim();

    // Filter bar JSX inlined at the render site (was a closure-defined
    // component, which React was remounting on every keystroke and dropping
    // input focus). See databaseFilterBar() below.
    const SubmarketSection = ({ title, count, activeLeaseGroups, expiredLeaseGroups, activeSaleGroups, expiredSaleGroups, CompCard: Card, forceOpen = false, onClose, hideHeader = false }) => {
      const [open, setOpen] = useState(forceOpen || !allCollapsed);
      // When this section is being driven by the card-grid expansion, ignore
      // the local toggle and always render open.
      const isOpen = forceOpen || open;

      const AddressGroup = ({ address, comps }) => {
        const [expanded, setExpanded] = useState(false);
        if (comps.length === 1) {
          return <div className="mb-2"><Card comp={comps[0]} /></div>;
        }
        // Multiple comps at same address — collapsible card with numbered list
        const first = comps[0];
        return (
          <div className="bg-[#2c2c2c] rounded-lg mb-3">
            <button onClick={() => setExpanded(!expanded)} className="w-full p-4 flex items-start justify-between text-left hover:bg-[rgba(58,58,58,0.3)] transition-colors rounded-lg">
              <div className="flex items-center gap-2">
                <svg className={`w-3.5 h-3.5 text-[#8a8a8a] transition-transform flex-shrink-0 ${expanded ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                <div>
                  <p className="text-white font-semibold text-sm">{first.property_name || first.address}</p>
                  {first.property_name && <p className="text-[#8a8a8a] text-xs">{first.address}{first.city ? `, ${first.city}` : ""}</p>}
                  {first.market && <p className="text-pp-red text-xs font-medium mt-0.5">{first.market}</p>}
                </div>
              </div>
              <span className="text-xs bg-[#3a3a3a] text-[#b4b4b4] rounded-full px-2 py-0.5 flex-shrink-0">{comps.length} comps</span>
            </button>
            {expanded && (
              <div className="px-4 pb-4">
                <div className="space-y-2">
                  {comps.map((c, i) => (
                    <div key={c.id} className="flex items-start gap-2 py-2 border-t border-[rgba(58,58,58,0.4)]">
                  <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                    {c.type === "lease" && leaseTypeBadge(c.lease_type)}
                    <span className="text-xs text-[#8a8a8a] font-bold w-4">{i + 1}.</span>
                  </div>
                  <div className="flex-1 grid grid-cols-4 gap-2 text-xs">
                    {c.type === "lease" ? (
                      <>
                        <div><span className="text-[#8a8a8a]">Tenant:</span> <span className="text-[#b4b4b4]">{c.tenant || "—"}</span></div>
                        <div><span className="text-[#8a8a8a]">Rate:</span> <span className="text-[#b4b4b4]">{c.lease_rate != null ? `$${Number(c.lease_rate).toFixed(2)}/SF` : "—"}</span></div>
                        <div><span className="text-[#8a8a8a]">SF:</span> <span className="text-[#b4b4b4]">{c.suite_sf ? Number(c.suite_sf).toLocaleString() : c.building_sf ? Number(c.building_sf).toLocaleString() : "—"}</span></div>
                        <div><span className="text-[#8a8a8a]">Date:</span> <span className="text-[#b4b4b4]">{fmtCompDate(c.comp_date)}</span></div>
                      </>
                    ) : (
                      <>
                        <div><span className="text-[#8a8a8a]">Price:</span> <span className="text-[#b4b4b4]">{c.sale_price ? `$${Number(c.sale_price).toLocaleString()}` : "—"}</span></div>
                        <div><span className="text-[#8a8a8a]">PSF:</span> <span className="text-[#b4b4b4]">{c.price_psf ? `$${Number(c.price_psf).toFixed(2)}` : "—"}</span></div>
                        <div><span className="text-[#8a8a8a]">Cap:</span> <span className="text-[#b4b4b4]">{c.cap_rate ? `${Number(c.cap_rate).toFixed(2)}%` : "—"}</span></div>
                        <div><span className="text-[#8a8a8a]">Date:</span> <span className="text-[#b4b4b4]">{fmtCompDate(c.comp_date)}</span></div>
                      </>
                    )}
                  </div>
                </div>
              ))}
                </div>
                <div className="flex items-center gap-2 mt-2 text-xs text-[#8a8a8a]">
                  {first.added_by ? <span>Added by {first.added_by}</span> : <span>Added by PPUSA</span>}
                </div>
              </div>
            )}
          </div>
        );
      };

      const TypeStatusGroup = ({ label, color, groups, defaultOpen: defOpen = true }) => {
        const [show, setShow] = useState(defOpen);
        const total = groups.reduce((s, [, g]) => s + g.length, 0);
        if (total === 0) return null;
        return (
          <div className="mb-4 ml-4">
            <button onClick={() => setShow(!show)} className="flex items-center gap-2 mb-2">
              <svg className={`w-3 h-3 text-[#8a8a8a] transition-transform ${show ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              <span className={`text-sm font-semibold ${color}`}>{label}</span>
              <span className="text-xs text-[#8a8a8a]">({total})</span>
            </button>
            {show && groups.map(([addr, comps]) => <AddressGroup key={addr} address={addr} comps={comps} />)}
          </div>
        );
      };

      // Use a different wrapper depending on whether we're standalone or
      // embedded inside the modal (hideHeader = true means the modal already
      // has its own branded header so we skip the inner title bar and the
      // colored left rule that doesn't fit the modal's white card layout).
      const wrapperCls = hideHeader
        ? ''
        : 'mb-6 border-l-4 border-pp-red/40 pl-4 bg-[rgba(44,44,44,0.4)] rounded-r-xl py-4 pr-4';
      return (
        <div className={wrapperCls}>
          {!hideHeader && (
            <div className="flex items-center gap-3 mb-3">
              {!forceOpen && (
                <button onClick={() => setOpen(!open)} className="flex items-center gap-3 flex-1 text-left">
                  <svg className={`w-4 h-4 text-[#8a8a8a] transition-transform ${open ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  <h3 className="text-lg font-bold text-pp-red font-montserrat">{title}</h3>
                  <span className="text-xs bg-[#3a3a3a] text-[#b4b4b4] rounded-full px-2 py-0.5">{count}</span>
                </button>
              )}
              {forceOpen && (
                <>
                  <h3 className="text-lg font-bold text-pp-red font-montserrat flex-1">{title}</h3>
                  <span className="text-xs bg-[#3a3a3a] text-[#b4b4b4] rounded-full px-2 py-0.5">{count}</span>
                  {onClose && (
                    <button onClick={onClose} className="text-[#8a8a8a] hover:text-white transition-colors text-lg leading-none px-2" title="Close">×</button>
                  )}
                </>
              )}
            </div>
          )}
          {isOpen && (
            <>
              <TypeStatusGroup label="Active Lease Comps" color="text-[#b4b4b4]" groups={activeLeaseGroups} />
              <TypeStatusGroup label="Active Sale Comps" color="text-[#f04f44]" groups={activeSaleGroups} />
              <TypeStatusGroup label="Expired Lease Comps" color="text-[#8a8a8a]" groups={expiredLeaseGroups} defaultOpen={false} />
              <TypeStatusGroup label="Expired Sale Comps" color="text-[#8a8a8a]" groups={expiredSaleGroups} defaultOpen={false} />
            </>
          )}
        </div>
      );
    };

    // Flat search-result row used when the user is filtering — denser than
    // the card grid so direct address matches surface at the top of the page.
    const CompRow = ({ comp }) => {
      const tracking = isTrackingComp(comp);
      const subline = [comp.city, comp.market, comp.micro_market].filter(Boolean).join(" · ");
      const metric = comp.type === "sale"
        ? [
            comp.sale_price ? `$${(comp.sale_price / 1_000_000).toFixed(2)}M` : null,
            comp.price_psf ? `$${Number(comp.price_psf).toFixed(0)} PSF` : null,
            comp.cap_rate ? `${Number(comp.cap_rate).toFixed(2)}% cap` : null,
          ].filter(Boolean).join(" · ") || "—"
        : [
            comp.tenant,
            comp.lease_rate ? `$${Number(comp.lease_rate).toFixed(2)}/SF` : null,
            comp.suite_sf ? `${Number(comp.suite_sf).toLocaleString()} SF` : (comp.building_sf ? `${Number(comp.building_sf).toLocaleString()} SF` : null),
          ].filter(Boolean).join(" · ") || "—";
      return (
        <div
          onClick={() => setExpandedCompId(comp.id)}
          className="grid grid-cols-12 gap-3 items-center px-3 py-2.5 bg-[rgba(44,44,44,0.6)] hover:bg-[rgba(58,58,58,0.6)] border border-[rgba(58,58,58,0.4)] rounded-lg cursor-pointer transition-colors"
        >
          <div className="col-span-1 flex items-center gap-1.5">
            <span className={badge(comp.type === "lease" ? "bg-[rgba(138,138,138,0.22)] text-[#b4b4b4]" : "bg-[rgba(233,53,41,0.2)] text-[#f04f44]")}>
              {comp.type === "lease" ? "Lease" : "Sale"}
            </span>
            {tracking && <span className={badge("bg-[rgba(233,53,41,0.25)] text-[#f04f44] border border-[rgba(233,53,41,0.4)]")}>Track</span>}
          </div>
          <div className="col-span-4 min-w-0">
            <p className="text-white text-sm font-semibold truncate">{comp.address || comp.property_name || "—"}</p>
            {subline && <p className="text-[#8a8a8a] text-xs truncate">{subline}</p>}
          </div>
          <div className="col-span-4 text-xs text-[#b4b4b4] truncate">{metric}</div>
          <div className="col-span-2 text-xs text-[#8a8a8a]">{fmtCompDate(comp.comp_date)}</div>
          <div className="col-span-1 flex items-center justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); viewCompOnMap(comp); }}
              className="text-[#8a8a8a] hover:text-pp-red transition-colors p-1"
              title="View on Map"
            >
              <MapPinIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    };

    // ── Comp drawer (single comp detail view) ────────────────────────────
    // Opened from a CompCard, a flat row, or the submarket modal. Shows the
    // full comp record + collapsible Notes + Mark as Traded for tracking comps.
    const expandedComp = expandedCompId ? validComps.find(c => c.id === expandedCompId) : null;
    const CompDrawer = ({ comp }) => {
      if (!comp) return null;
      const tracking = isTrackingComp(comp);
      const fmtMoney = (n) => n != null ? `$${Number(n).toLocaleString()}` : "—";
      const fmtSf = (n) => n != null ? `${Number(n).toLocaleString()} SF` : "—";
      const fmtPct = (n) => n != null ? `${Number(n).toFixed(1)}%` : "—";
      const longNotes = (comp.notes || "").length > 200;
      return (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-[rgba(36,36,36,0.4)] backdrop-blur-sm overflow-y-auto p-4 sm:p-8" onClick={() => { setExpandedCompId(null); setMarkTradedForm(null); }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-3xl my-4 overflow-hidden" style={{ backgroundColor: '#ffffff', border: '1px solid #d8d8d8' }} onClick={(e) => e.stopPropagation()}>
            {/* Branded header */}
            <div className="flex items-start justify-between px-6 py-4" style={{ borderBottom: '1px solid #d8d8d8', backgroundColor: '#ffffff' }}>
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-2 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: '#e93529' }} />
                <div className="min-w-0">
                  <h2 className="text-lg font-bold font-montserrat truncate" style={{ color: '#333333' }}>
                    {comp.property_name || comp.address}
                  </h2>
                  {comp.property_name && (
                    <p className="text-xs mt-0.5 truncate" style={{ color: '#6a6a6a' }}>{comp.address}{comp.city ? `, ${comp.city}` : ""}{comp.state ? ` ${comp.state}` : ""}</p>
                  )}
                  <div className="flex items-center flex-wrap gap-2 mt-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8a8a8a' }}>
                      {comp.market || "—"}{comp.micro_market ? ` · ${comp.micro_market}` : ""}
                    </span>
                    {tracking && <span className={badge("bg-[rgba(233,53,41,0.2)] text-[#c42d22] border border-[rgba(233,53,41,0.4)]")}>Tracking</span>}
                    {comp.status_detail && !tracking && <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#f4f4f4', color: '#555555' }}>{comp.status_detail}</span>}
                    <span className={badge(comp.type === "lease" ? "bg-[rgba(138,138,138,0.22)] text-[#555555]" : "bg-[rgba(233,53,41,0.2)] text-[#c42d22]")}>
                      {comp.type === "lease" ? "Lease" : "Sale"} · {comp.status === "active" ? "Active" : "Expired"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                <button
                  onClick={() => viewCompOnMap(comp)}
                  className="flex items-center gap-1.5 bg-pp-red hover:bg-pp-red-light text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                  style={{ color: '#ffffff' }}
                  title="View on Map"
                >
                  <MapPinIcon /> View on Map
                </button>
                <button
                  onClick={() => { setExpandedCompId(null); setMarkTradedForm(null); }}
                  className="rounded-lg w-8 h-8 flex items-center justify-center hover:bg-[#ebebeb]"
                  style={{ color: '#6a6a6a' }}
                  title="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5" style={{ backgroundColor: '#f4f4f4', maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
              {/* Sale comp layout */}
              {comp.type === "sale" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <DrawerStat label="Price" value={fmtMoney(comp.sale_price)} />
                    <DrawerStat label="PSF" value={comp.price_psf ? `$${Number(comp.price_psf).toFixed(2)}` : "—"} />
                    <DrawerStat label="Cap Rate" value={comp.cap_rate ? `${Number(comp.cap_rate).toFixed(2)}%` : "—"} />
                    <DrawerStat label="Date" value={comp.comp_date || "—"} />
                    <DrawerStat label="Building SF" value={fmtSf(comp.building_sf)} />
                    <DrawerStat label="Source" value={comp.source || "—"} />
                  </div>
                  {(comp.seller || comp.buyer) && (
                    <div className="rounded-lg px-4 py-3" style={{ backgroundColor: '#ffffff', border: '1px solid #d8d8d8' }}>
                      <div className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#8a8a8a' }}>Trade</div>
                      <div className="text-sm font-medium" style={{ color: '#333333' }}>
                        <span>{comp.seller || "—"}</span>
                        <span className="mx-2 text-pp-red font-bold">→</span>
                        <span>{comp.buyer || "—"}</span>
                      </div>
                      {comp.portfolio_name && <div className="text-xs mt-1" style={{ color: '#6a6a6a' }}>Portfolio: {comp.portfolio_name}</div>}
                    </div>
                  )}
                  {(comp.year_built || comp.clear_height || comp.building_sf) && (
                    <div className="grid grid-cols-3 gap-4">
                      <DrawerStat label="Year Built" value={comp.year_built || "—"} />
                      <DrawerStat label="Clear Height" value={comp.clear_height || "—"} />
                      <DrawerStat label="Office Finish" value={comp.office_finish_pct != null ? fmtPct(comp.office_finish_pct) : "—"} />
                    </div>
                  )}
                  {(comp.occupancy_at_closing != null || comp.walt_years != null || comp.office_finish_pct != null) && (
                    <div className="grid grid-cols-3 gap-4">
                      <DrawerStat label="Occupancy at Closing" value={comp.occupancy_at_closing != null ? fmtPct(comp.occupancy_at_closing) : "—"} />
                      <DrawerStat label="WALT" value={comp.walt_years != null ? `${Number(comp.walt_years).toFixed(1)} yrs` : "—"} />
                      <DrawerStat label="Status Detail" value={comp.status_detail || "—"} />
                    </div>
                  )}
                </div>
              )}

              {/* Lease comp layout */}
              {comp.type === "lease" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <DrawerStat label="Tenant" value={comp.tenant || "—"} />
                    <DrawerStat label="Rate" value={comp.lease_rate != null ? `$${Number(comp.lease_rate).toFixed(2)}/SF` : "—"} />
                    <DrawerStat label="Lease Type" value={comp.lease_type || "—"} />
                    <DrawerStat label="OPEX $/SF" value={comp.opex_psf != null ? `$${Number(comp.opex_psf).toFixed(2)}` : "—"} />
                    <DrawerStat label="Suite SF" value={fmtSf(comp.suite_sf)} />
                    <DrawerStat label="Building SF" value={fmtSf(comp.building_sf)} />
                    <DrawerStat label="Date" value={comp.comp_date || "—"} />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <DrawerStat label="Landlord" value={comp.landlord || "—"} />
                    <DrawerStat label="Tenant Industry" value={comp.tenant_industry || "—"} />
                    <DrawerStat label="Source" value={comp.source || "—"} />
                  </div>
                  {(comp.year_built || comp.clear_height || comp.loading_type || comp.door_type) && (
                    <div className="grid grid-cols-4 gap-4">
                      <DrawerStat label="Year Built" value={comp.year_built || "—"} />
                      <DrawerStat label="Clear Height" value={comp.clear_height || "—"} />
                      <DrawerStat label="Loading" value={comp.loading_type || "—"} />
                      <DrawerStat label="Door Type" value={comp.door_type || "—"} />
                    </div>
                  )}
                </div>
              )}

              {/* Notes — collapsible if long, preserves line breaks */}
              {comp.notes && (
                <div className="mt-5">
                  <CollapsibleSection title="Notes" defaultOpen={!longNotes}>
                    <pre className="text-sm whitespace-pre-wrap font-montserrat leading-relaxed" style={{ color: '#333333' }}>{comp.notes}</pre>
                  </CollapsibleSection>
                </div>
              )}

              {/* Mark as Traded — only for Tracking comps with no realized price */}
              {tracking && !comp.sale_price && (
                <div className="mt-5 rounded-lg p-4" style={{ backgroundColor: '#faf4e8', border: '1px solid #c8922f' }}>
                  {!markTradedForm || markTradedForm.compId !== comp.id ? (
                    <button
                      onClick={() => setMarkTradedForm({ compId: comp.id, sale_price: "", comp_date: "", buyer: comp.buyer || "", cap_rate: "", notes: "" })}
                      className="bg-pp-red hover:bg-pp-red-light text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                      style={{ color: '#ffffff' }}
                    >
                      ✓ Mark as Traded
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-sm font-semibold" style={{ color: '#8a6a1f' }}>Mark as Traded</div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Trade Date" required>
                          <input type="date" value={markTradedForm.comp_date}
                            onChange={(e) => setMarkTradedForm(f => ({ ...f, comp_date: e.target.value }))}
                            className={inputCls()} />
                        </Field>
                        <Field label="Sale Price ($)" required>
                          <input type="number" value={markTradedForm.sale_price}
                            onChange={(e) => setMarkTradedForm(f => ({ ...f, sale_price: e.target.value }))}
                            className={inputCls()} />
                        </Field>
                        <Field label="Buyer">
                          <input value={markTradedForm.buyer}
                            onChange={(e) => setMarkTradedForm(f => ({ ...f, buyer: e.target.value }))}
                            className={inputCls()} />
                        </Field>
                        <Field label="Cap Rate (%)">
                          <input type="number" step="0.01" value={markTradedForm.cap_rate}
                            onChange={(e) => setMarkTradedForm(f => ({ ...f, cap_rate: e.target.value }))}
                            className={inputCls()} />
                        </Field>
                      </div>
                      <Field label="Notes (optional)">
                        <textarea rows={2} value={markTradedForm.notes}
                          onChange={(e) => setMarkTradedForm(f => ({ ...f, notes: e.target.value }))}
                          className={inputCls() + " resize-none"} />
                      </Field>
                      <div className="flex gap-2">
                        <button onClick={submitMarkTraded} disabled={saving}
                          className="bg-pp-red hover:bg-pp-red-light disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                          style={{ color: '#ffffff' }}>
                          {saving ? "Saving..." : "Save Trade"}
                        </button>
                        <button onClick={() => setMarkTradedForm(null)}
                          className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#f4f4f4] text-sm px-4 py-2 rounded-lg transition-colors">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Footer meta */}
              <div className="flex items-center justify-between mt-5 text-xs" style={{ color: '#8a8a8a' }}>
                <span>{comp.added_by ? `Added by ${comp.added_by}` : "Added by PPUSA"}</span>
                {comp.from_deal_id && <span>From pipeline deal</span>}
              </div>
            </div>
          </div>
        </div>
      );
    };

    // Decide whether to show flat list or submarket cards.
    // Per V17 spec: any active search OR a tight filter (<50 comps) flattens.
    const flatten = !!dbSearch.trim() || filteredComps.length < 50;

    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        {toast && <div className="fixed top-4 right-4 z-50 bg-[rgba(79,157,118,0.2)] text-[#7cbb99] border border-[rgba(79,157,118,0.3)] px-4 py-2 rounded-lg text-sm shadow-lg">{toast}</div>}
        {expandedComp && <CompDrawer comp={expandedComp} />}

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => setView("home")} className="text-[#8a8a8a] hover:text-white text-sm">← Back</button>
            <h2 className="text-2xl font-bold text-white font-montserrat">Comp Database</h2>
            <span className="text-[#8a8a8a] text-sm">{comps.length} total</span>
          </div>
          <button onClick={() => setView("add-home")} className="bg-pp-red hover:bg-pp-red-light text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors" style={{ color: '#fff' }}>
            + Add Comp
          </button>
        </div>

        {/* Filter bar — inlined (not a child component) so the search input
            keeps focus across keystrokes. */}
        <div className="mb-6 bg-[rgba(44,44,44,0.5)] border border-[rgba(58,58,58,0.4)] rounded-xl p-3 flex flex-wrap items-center gap-2">
          <input value={dbSearch} onChange={e => setDbSearch(e.target.value)} placeholder="Search address, tenant, buyer..."
            className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-[#8a8a8a] w-56 focus:outline-none focus:border-pp-red" />
          <select value={dbTypeFilter} onChange={e => setDbTypeFilter(e.target.value)}
            className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-pp-red">
            <option value="all">All Types</option>
            <option value="lease">Lease Only</option>
            <option value="sale">Sale Only</option>
          </select>
          <select value={dbStatusFilter} onChange={e => setDbStatusFilter(e.target.value)}
            className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-pp-red">
            <option value="all">Active & Expired</option>
            <option value="active">Active Only</option>
            <option value="expired">Expired Only</option>
          </select>
          <select value={dbMarketFilter} onChange={e => setDbMarketFilter(e.target.value)}
            className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-pp-red">
            <option value="all">All Markets</option>
            {uniqueMarkets.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={trackingFilter} onChange={e => setTrackingFilter(e.target.value)}
            className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-pp-red"
            title="Tracking comps are pipeline deals we passed on but want to monitor — excluded by default so they don't muddy market averages.">
            <option value="exclude">Hide Tracking</option>
            <option value="only">Tracking Only</option>
            <option value="include">Include Tracking</option>
          </select>
          <button onClick={() => setAllCollapsed(v => !v)}
            className="bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#b4b4b4] text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
            {allCollapsed ? "Expand All" : "Collapse All"}
          </button>
          {hasActiveFilters && (
            <button onClick={() => { setDbSearch(""); setDbTypeFilter("all"); setDbStatusFilter("all"); setDbMarketFilter("all"); setTrackingFilter("exclude"); }}
              className="text-pp-red hover:text-pp-red-light text-xs font-semibold px-2 py-1.5 transition-colors">
              Clear Filters
            </button>
          )}
          <span className="text-[#8a8a8a] text-xs ml-auto">{filteredComps.length} of {validComps.length} comps</span>
        </div>

        {/* Portfolios appear ABOVE the submarket grid in the default browse
            view, but BELOW the flat list when the user is searching, so
            direct address matches are surfaced first. */}
        {portfolios.length > 0 && !flatten && (
          <PortfolioGrid portfolios={portfolios} badge={badge} sectionTitle={sectionTitle} onSelect={setExpandedPortfolio} />
        )}

        {/* ── FLAT SEARCH LIST ────────────────────────────────────────── */}
        {flatten && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-[#b4b4b4]">
                <span className="font-semibold">{filteredComps.length}</span> result{filteredComps.length === 1 ? "" : "s"}
                {dbSearch.trim() && <> for <span className="text-pp-red font-semibold">"{dbSearch}"</span></>}
              </div>
              {hasActiveFilters && (
                <button
                  onClick={() => { setDbSearch(""); setDbTypeFilter("all"); setDbStatusFilter("all"); setDbMarketFilter("all"); setTrackingFilter("exclude"); }}
                  className="text-pp-red hover:text-pp-red-light text-xs font-semibold transition-colors"
                >
                  Clear filter
                </button>
              )}
            </div>
            {filteredComps.length === 0 ? (
              <div className="text-[#8a8a8a] text-sm bg-[rgba(44,44,44,0.4)] border border-[rgba(58,58,58,0.4)] rounded-lg p-6 text-center">
                No comps match your filter.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {filteredComps
                  .slice()
                  .sort((a, b) => (a.address || "").localeCompare(b.address || ""))
                  .map(c => <CompRow key={c.id} comp={c} />)}
              </div>
            )}
            {portfolios.length > 0 && (
              <div className="mt-8">
                <PortfolioGrid portfolios={portfolios} badge={badge} sectionTitle={sectionTitle} onSelect={setExpandedPortfolio} />
              </div>
            )}
          </div>
        )}

        {/* Portfolio detail modal — branded white card listing every asset */}
        {expandedPortfolio && (() => {
          const { group, totalPrice, totalSF, avgPsf, avgCap } = expandedPortfolio;
          return (
            <div
              className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(36,36,36,0.4)] backdrop-blur-sm overflow-y-auto p-4 sm:p-8"
              onClick={() => setExpandedPortfolio(null)}
            >
              <div
                className="rounded-2xl shadow-2xl w-full max-w-5xl my-4 overflow-hidden"
                style={{ backgroundColor: '#ffffff', border: '1px solid #d8d8d8' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #d8d8d8', backgroundColor: '#ffffff' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-8 rounded-full" style={{ backgroundColor: '#e93529' }} />
                    <div>
                      <h2 className="text-xl font-bold font-montserrat" style={{ color: '#333333' }}>{group[0].buyer}</h2>
                      <p className="text-xs font-semibold uppercase tracking-wider mt-0.5" style={{ color: '#8a8a8a' }}>
                        Portfolio Sale · {group[0].comp_date} · {group.length} assets
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedPortfolio(null)}
                    className="rounded-lg w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#ebebeb]"
                    style={{ color: '#6a6a6a' }}
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>

                {/* Aggregate stats bar */}
                <div className="grid grid-cols-4 gap-3 px-6 py-4" style={{ backgroundColor: '#f4f4f4', borderBottom: '1px solid #d8d8d8' }}>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#888' }}>Total Price</div>
                    <div className="text-base font-bold" style={{ color: '#333' }}>${Number(totalPrice).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#888' }}>Total SF</div>
                    <div className="text-base font-bold" style={{ color: '#333' }}>{Number(totalSF).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#888' }}>Avg PSF</div>
                    <div className="text-base font-bold" style={{ color: '#e93529' }}>${avgPsf}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#888' }}>Avg Cap</div>
                    <div className="text-base font-bold" style={{ color: '#e93529' }}>{avgCap ? `${avgCap}%` : '—'}</div>
                  </div>
                </div>

                {/* Asset list */}
                <div className="px-6 py-5" style={{ backgroundColor: '#f4f4f4', maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
                  <div className="space-y-2">
                    {group.map((c, idx) => {
                      const psf = c.price_psf || (c.sale_price && c.building_sf ? Math.round(c.sale_price / c.building_sf) : null);
                      return (
                        <div key={c.id || idx} className="rounded-lg p-4" style={{ backgroundColor: '#ffffff', border: '1px solid #e4e4e4' }}>
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-sm truncate" style={{ color: '#333' }}>{c.property_name || c.address}</div>
                              {c.property_name && <div className="text-xs mt-0.5" style={{ color: '#666' }}>{c.address}{c.city ? `, ${c.city}` : ''}{c.state ? `, ${c.state}` : ''}</div>}
                              {c.market && <div className="text-xs font-semibold mt-1" style={{ color: '#e93529' }}>{c.market}</div>}
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-wider whitespace-nowrap" style={{ color: '#888' }}>#{idx + 1}</span>
                          </div>
                          <div className="grid grid-cols-4 gap-3 text-xs pt-2" style={{ borderTop: '1px solid #f4f4f4' }}>
                            <div><span style={{ color: '#888' }}>Price:</span> <span className="font-semibold" style={{ color: '#333' }}>{c.sale_price ? `$${Number(c.sale_price).toLocaleString()}` : '—'}</span></div>
                            <div><span style={{ color: '#888' }}>SF:</span> <span className="font-semibold" style={{ color: '#333' }}>{c.building_sf ? Number(c.building_sf).toLocaleString() : '—'}</span></div>
                            <div><span style={{ color: '#888' }}>PSF:</span> <span className="font-semibold" style={{ color: '#333' }}>{psf ? `$${psf}` : '—'}</span></div>
                            <div><span style={{ color: '#888' }}>Cap:</span> <span className="font-semibold" style={{ color: '#333' }}>{c.cap_rate ? `${Number(c.cap_rate).toFixed(2)}%` : '—'}</span></div>
                          </div>
                          {c.seller && <div className="text-xs mt-2" style={{ color: '#666' }}><span style={{ color: '#888' }}>Seller:</span> {c.seller}</div>}
                          {c.notes && <div className="text-xs mt-1" style={{ color: '#666' }}>{c.notes}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Grouped by Submarket — rendered as a responsive card grid. Click
            a card to expand that submarket inline (Type → Status → Address). */}
        {!flatten && (() => {
          const submarketMap = {};
          filteredComps.forEach(c => {
            const sub = c.market || "Other";
            if (!submarketMap[sub]) submarketMap[sub] = [];
            submarketMap[sub].push(c);
          });
          const sortedSubmarkets = Object.entries(submarketMap)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .filter(([, subComps]) => subComps.length > 0);

          if (sortedSubmarkets.length === 0) return null;

          return (
            <>
              {/* Card grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                {sortedSubmarkets.map(([submarket, subComps]) => {
                  const leases = subComps.filter(c => c.type === "lease").length;
                  const sales = subComps.filter(c => c.type === "sale").length;
                  const isOpen = expandedSubmarket === submarket;
                  return (
                    <button
                      key={submarket}
                      onClick={() => setExpandedSubmarket(isOpen ? null : submarket)}
                      className={`text-left bg-[#2c2c2c] hover:bg-[#3a3a3a] border ${isOpen ? "border-pp-red ring-2 ring-pp-red/30" : "border-[#3a3a3a]"} rounded-xl px-4 py-3 transition-all`}
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <h3 className="text-sm font-bold text-pp-red font-montserrat truncate">{submarket}</h3>
                        <span className="text-xs bg-[#3a3a3a] text-[#b4b4b4] rounded-full px-2 py-0.5 flex-shrink-0">{subComps.length}</span>
                      </div>
                      <div className="flex gap-3 text-[11px] text-[#8a8a8a]">
                        {leases > 0 && <span><span className="text-[#b4b4b4] font-semibold">{leases}</span> lease</span>}
                        {sales > 0 && <span><span className="text-[#f04f44] font-semibold">{sales}</span> sale</span>}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Expanded comps render as a modal overlay so they're visible
                  regardless of how many submarket cards are in the grid. */}
              {expandedSubmarket && (() => {
                const subComps = submarketMap[expandedSubmarket] || [];
                const leases = subComps.filter(c => c.type === "lease");
                const sales = subComps.filter(c => c.type === "sale");
                const groupByAddress = (items) => {
                  const map = {};
                  items.sort((a, b) => (a.address || "").localeCompare(b.address || "")).forEach(c => {
                    const key = (c.address || "Unknown").toLowerCase().trim();
                    if (!map[key]) map[key] = [];
                    map[key].push(c);
                  });
                  return Object.entries(map);
                };
                const activeLeaseGroups = groupByAddress(leases.filter(c => c.status === "active"));
                const expiredLeaseGroups = groupByAddress(leases.filter(c => c.status === "expired"));
                const activeSaleGroups = groupByAddress(sales.filter(c => c.status === "active"));
                const expiredSaleGroups = groupByAddress(sales.filter(c => c.status === "expired"));
                return (
                  <div
                    className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(36,36,36,0.4)] backdrop-blur-sm overflow-y-auto p-4 sm:p-8"
                    onClick={() => setExpandedSubmarket(null)}
                  >
                    <div
                      className="rounded-2xl shadow-2xl w-full max-w-5xl my-4 overflow-hidden"
                      style={{ backgroundColor: '#ffffff', border: '1px solid #d8d8d8' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Modal header — branded */}
                      <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #d8d8d8', backgroundColor: '#ffffff' }}>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-8 rounded-full" style={{ backgroundColor: '#e93529' }} />
                          <div>
                            <h2 className="text-xl font-bold font-montserrat" style={{ color: '#333333' }}>{expandedSubmarket}</h2>
                            <p className="text-xs font-semibold uppercase tracking-wider mt-0.5" style={{ color: '#8a8a8a' }}>
                              {subComps.length} comp{subComps.length === 1 ? '' : 's'}
                              {leases.length > 0 && ` · ${leases.length} lease`}
                              {sales.length > 0 && ` · ${sales.length} sale`}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setExpandedSubmarket(null)}
                          className="rounded-lg w-8 h-8 flex items-center justify-center transition-colors hover:bg-[#ebebeb]"
                          style={{ color: '#6a6a6a' }}
                          title="Close"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>

                      {/* Modal body */}
                      <div className="px-6 py-5" style={{ backgroundColor: '#f4f4f4', maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                        <SubmarketSection
                          key={expandedSubmarket}
                          title={expandedSubmarket}
                          count={subComps.length}
                          forceOpen
                          hideHeader
                          activeLeaseGroups={activeLeaseGroups}
                          expiredLeaseGroups={expiredLeaseGroups}
                          activeSaleGroups={activeSaleGroups}
                          expiredSaleGroups={expiredSaleGroups}
                          CompCard={CompCard}
                          badge={badge}
                          fmtCompDate={fmtCompDate}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          );
        })()}

        {/* Missing Data */}
        {missingData.length > 0 && (
          <Section title="Missing Data" items={missingData} color="bg-[rgba(233,53,41,0.2)] text-[#f04f44]" defaultOpen={false} />
        )}
      </div>
    );
  }

  return null;
}

// ─── AwaitingSaleModal ────────────────────────────────────────────────
// Records what a passed/lost/dead deal actually traded for so we can
// compare our bid to where the market cleared. Saves straight to the
// `deals` table — no separate market-clearing table for now.
function AwaitingSaleModal({ deal, onClose, onSaved }) {
  const [salePrice, setSalePrice] = useState(deal.market_cleared_price ?? "");
  const [pricePsf, setPricePsf]   = useState(deal.market_cleared_price_psf ?? "");
  const [buyer, setBuyer]         = useState(deal.market_cleared_buyer ?? "");
  const [closeDate, setCloseDate] = useState(deal.market_cleared_at ?? "");
  const [notes, setNotes]         = useState(deal.market_cleared_notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sf = Number(deal.square_footage) || 0;

  // Two-way binding between $ total and $/SF so the analyst can enter
  // whichever they have from the closing news / broker debrief.
  function setSaleBoth(v) {
    setSalePrice(v);
    if (sf > 0 && v) setPricePsf((Number(v) / sf).toFixed(2));
  }
  function setPsfBoth(v) {
    setPricePsf(v);
    if (sf > 0 && v) setSalePrice(Math.round(Number(v) * sf));
  }

  async function save() {
    setSaving(true); setError(null);
    const patch = {
      market_cleared_price:     salePrice === "" ? null : Number(salePrice),
      market_cleared_price_psf: pricePsf  === "" ? null : Number(pricePsf),
      market_cleared_buyer:     buyer?.trim() || null,
      market_cleared_at:        closeDate    || null,
      market_cleared_notes:     notes?.trim() || null,
    };
    const { error: e } = await supabase.from("deals").update(patch).eq("id", deal.id);
    setSaving(false);
    if (e) { setError(e.message); return; }
    onSaved();
  }

  let firstAddr;
  try { const p = JSON.parse(deal.address); firstAddr = Array.isArray(p) ? p[0] : deal.address; }
  catch { firstAddr = deal.address; }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-[#242424] border border-[#3a3a3a] rounded-2xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-lg font-bold text-white font-montserrat">Record Market Clearing Sale</h3>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-[#f4f4f4]">✕</button>
        </div>
        <div className="text-xs text-[#8a8a8a] mb-1">{firstAddr}</div>
        <div className="text-[11px] text-[#8a8a8a] mb-4">
          {deal.deal_stage} · {sf ? `${sf.toLocaleString()} SF` : "SF unknown"}
          {deal.killed_reason && <> · killed: <span className="italic">{deal.killed_reason}</span></>}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-[#8a8a8a] mb-1">Sale Price ($)</label>
            <input type="number" value={salePrice} onChange={(e) => setSaleBoth(e.target.value)}
              className={inputCls()} placeholder="e.g. 21000000" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-[#8a8a8a] mb-1">Sale $/SF</label>
            <input type="number" step="0.01" value={pricePsf} onChange={(e) => setPsfBoth(e.target.value)}
              className={inputCls()} placeholder="e.g. 203.88" />
          </div>
        </div>

        <label className="block text-xs uppercase tracking-wider font-bold text-[#8a8a8a] mb-1">Buyer</label>
        <input type="text" value={buyer} onChange={(e) => setBuyer(e.target.value)}
          className={inputCls() + " mb-3"} placeholder="e.g. East Group" />

        <label className="block text-xs uppercase tracking-wider font-bold text-[#8a8a8a] mb-1">Close Date</label>
        <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)}
          className={inputCls() + " mb-3"} />

        <label className="block text-xs uppercase tracking-wider font-bold text-[#8a8a8a] mb-1">Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
          className={inputCls() + " mb-3"}
          placeholder="How did we learn? What was the story?" />

        {error && <div className="text-xs text-[#f04f44] mb-2">{error}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#b4b4b4] hover:text-[#f4f4f4]">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 text-sm rounded-lg bg-pp-red hover:bg-pp-red-light text-white font-semibold disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
